/**
 * Derivazioni pure della sezione Limulo: nessuna rete, nessun orologio letto di nascosto
 * (l'istante arriva come parametro), nessun `import.meta.env`. Sono le funzioni che
 * `tools/verify-limulo.ts` puo' esercitare sotto Node — e sono anche le uniche che
 * decidono cosa vede l'utente quando i dati non hanno la forma che ci si aspettava.
 *
 * La regola che percorre tutto il file: **cio' che non sappiamo non diventa un fatto.**
 * Una data illeggibile non diventa «non scade mai», un conteggio assente non diventa 0,
 * un identificatore di gioco non diventa un nome, un torneo che non capiamo non sparisce
 * dalla lista.
 */
import type {
  LimuloFavoriteGame,
  LimuloFavoriteGames,
  LimuloGame,
  LimuloMatch,
  LimuloMatchCheckin,
  LimuloMatchCheckInOutcome,
  LimuloMatchCheckInState,
  LimuloMatchParticipant,
  LimuloMatchResult,
  LimuloMatchResultPayload,
  LimuloMediaAsset,
  LimuloPagination,
  LimuloParticipant,
  LimuloPrizeBand,
  LimuloResultMediaAttach,
  LimuloRuleFile,
  LimuloSchedule,
  LimuloStandingsRow,
  LimuloTournament,
  LimuloWindow,
} from '../../types/limulo.ts';
import { scadenzaRisultatoOf } from './scadenzaRisultato.ts';

const asRecord = (value: unknown): Record<string, unknown> =>
  value !== null && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};

const asString = (value: unknown): string => (typeof value === 'string' ? value : '');

const asStringArray = (value: unknown): string[] =>
  Array.isArray(value) ? value.filter((item): item is string => typeof item === 'string') : [];

const asFiniteNumber = (value: unknown): number | null =>
  typeof value === 'number' && Number.isFinite(value) ? value : null;

/** Il 2000 e il 2100 come estremi: fuori di li' non e' una data, e' un numero. */
const INSTANT_FLOOR = Date.UTC(2000, 0, 1);
const INSTANT_CEILING = Date.UTC(2100, 0, 1);

/**
 * Una data dal filo → epoch ms, oppure `null` («non lo so»).
 *
 * ⚠️ IL NUMERO NON E' IL «RAMO LEGACY»: e' anche il nativo. `app-qtournaments` dichiara
 * le date come numeri (`persistenza/tournament.model.js:383` →
 * `publishAt: { type: Number, default: null }`), le scrive come numeri
 * (`core/creazione.js:251`) e il sanitizer del BFF le passa senza convertirle
 * (`policy.js:263`: `tournament.schedule.publishAt || null`). Misurato su
 * `qlash-limulo@91743eb` — difetto segnalato da or35, prova aggiunta da tma-orcr.
 *
 * 🔴 E QUESTA FUNZIONE E' L'UNICA. Fino al 2026-09-02 ce n'erano DUE: questa, esportata,
 * con dodici asserzioni verdi — e un `instant()` privato che faceva
 * `Date.parse(asString(v))` e sui numeri restituiva `null`. Le funzioni vive usavano il
 * secondo. Il parser giusto non era mai stato collegato: il tipo ammetteva un caso che
 * il codice scartava, e il gate era verde su una funzione che nessuno chiamava.
 * Due parser per la stessa domanda sono un difetto anche quando entrambi sono corretti,
 * perche' basta che uno solo venga usato.
 *
 * ⚠️ ASSUNZIONE DICHIARATA: il numero e' interpretato come **millisecondi**. E' la
 * convenzione JS ed e' cio' che usa il provider, che confronta `publishAt` con
 * `Date.now()`. Se un giorno arrivassero SECONDI, cadrebbero nel 1970 — e la finestra
 * di sanita' li rende `null` invece di lasciarli diventare una data assurda.
 * La scelta e' deliberata e ha un costo: un `null` e' silenzioso, un «gennaio 1970» a
 * schermo salterebbe all'occhio subito. Si preferisce il silenzio perche' `null` qui non
 * e' l'assenza di una risposta — e' la risposta «non lo so», che la UI mostra come «date
 * non dichiarate» su OGNI riga; se succedesse davvero, sarebbe visibile lo stesso, senza
 * dire all'utente una cosa falsa nel frattempo.
 */
export function normalizeInstant(value: unknown): number | null {
  const ms =
    typeof value === 'number'
      ? value
      : Number.isFinite(Date.parse(asString(value)))
        ? Date.parse(asString(value))
        : NaN;
  if (!Number.isFinite(ms)) return null;
  return ms >= INSTANT_FLOOR && ms < INSTANT_CEILING ? ms : null;
}

// ── L'envelope ───────────────────────────────────────────────────────────────
// `{ok, organizationId, data, meta}` e, per le liste, `data: {items: [...]}` con la
// `pagination` FUORI da `data` (`router.js:73-92`). Nessuno degli altri client di questa
// app ha questa forma: non c'e' un unwrapper esistente da riusare.

export function unwrapData<T>(envelope: unknown): T | null {
  const record = asRecord(envelope);
  return 'data' in record ? (record.data as T) : null;
}

/**
 * **Il payload di una capability di un GAME HOST** (scacchi, burraco), dal `data` della busta del BFF (I-545).
 *
 * 🔴 Il difetto (Luca, 08/10, una Sfida a scacchi fra due suoi account: «Non riesco a collegarmi al tavolo: il servizio non
 * ha dato il permesso di entrare», `TICKET_UNAVAILABLE`): i game host rispondono con `ok = (data, extra) => ({ provider,
 * ...extra, data })` (QChess@98944756 `limulo-app/backend/resolvers.js:39` e `:246`; QBurraco@2e29953e `:42` e `:218`), e il
 * BFF lo rende com'e'. Quindi `body.data` — cio' che rende `unwrapData` — e' `{ provider, data: { ticket, expiresInSeconds,
 * wsUrl, purpose } }`: il ticket sta UN LIVELLO PIU' IN BASSO, e `chessTicketGrantOf` non lo trovava e rendeva `null`. Il web
 * (`qlash-web`, `lib/qchess-bff.ts` `livelloCon`) lo legge scendendo, e il tavolo si apre.
 *
 * La forma si riconosce dal CONTENUTO, non dal nome della rotta: `data` ha `provider` stringa e un `data` oggetto ⇒ si rende
 * quest'ultimo; ogni altra cosa (la forma piatta di prima, `null`, una lista) si rende com'e'. Un `data` senza `provider`
 * che per caso porta una chiave `data` NON e' un involucro di game host e non si scende.
 * ⚠️ Si usa SOLO dove la capability e' di un game host (`issueChessTicket`, `issueBurracoTicket`): le altre rotte hanno la
 * loro forma e un `unwrapData` che scendesse da solo cambierebbe quello che leggono. Le altre letture di
 * `/v1/games/chess/*` e `/v1/games/burraco/*` che passano da `unwrapData` sono in APERTO nella PR di I-545, non corrette qui.
 */
export function capabilityPayloadOf(data: unknown): unknown {
  const r = asRecord(data);
  const interno = r.data;
  const eOggetto = interno !== null && typeof interno === 'object' && !Array.isArray(interno);
  return typeof r.provider === 'string' && eOggetto ? interno : data;
}

export function unwrapItems<T>(envelope: unknown): T[] {
  const items = asRecord(asRecord(envelope).data).items;
  return Array.isArray(items) ? (items as T[]) : [];
}

/**
 * ✅ **LA SUPERFICIE E' ARRIVATA — 2026-09-20.** Qui c'era scritto, dal 2026-09-02:
 *
 *   > *«NESSUN CONSUMATORE OGGI, e la distinzione conta: questa e' una capacita' in attesa
 *   > di una superficie, non un residuo. Chi la trova non la cancelli come codice morto:
 *   > "non ancora usata" e "non piu' usata" si assomigliano e portano a decisioni opposte.»*
 *
 * ⭐ **La riga e' mostrata invece di cancellata perche' la sua scommessa e' stata pagata**:
 * la lista tornei (reference A1, I-92) la consuma da oggi, e la funzione era li', giusta,
 * ad aspettare. Chi trova la prossima «capacita' in attesa» sappia che qualcuna arriva.
 *
 * `buildPagination` (`router.js:121-131`) lascia `total` e `totalPages` `undefined` quando
 * il provider non li manda. Diventano `null`, che e' una domanda senza risposta: mostrare
 * «pagina 2 di 1» sarebbe peggio che non mostrare niente.
 */
export function unwrapPagination(envelope: unknown): LimuloPagination | null {
  const raw = asRecord(envelope).pagination;
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const pagination = raw as Record<string, unknown>;
  return {
    page: asFiniteNumber(pagination.page) ?? 1,
    limit: asFiniteNumber(pagination.limit) ?? 0,
    total: asFiniteNumber(pagination.total),
    totalPages: asFiniteNumber(pagination.totalPages),
  };
}

// ── Il torneo ────────────────────────────────────────────────────────────────

/**
 * `sanitizePublicTournament` (`policy.js:229-237`) emette sia `title` sia `name`, ognuno
 * col fallback sull'altro. Sono quasi sempre uguali; quando non lo sono serve una regola
 * fissa, altrimenti due schermate mostrano due nomi diversi per lo stesso torneo.
 */
export function tournamentTitle(tournament: unknown): string {
  const t = asRecord(tournament);
  return asString(t.title) || asString(t.name);
}

/**
 * ⚠️ PASSTHROUGH, vincolo kb#81. Si restituisce l'identificatore di gioco COSI' COM'E',
 * senza risolverlo contro `app-games`: una seconda chiamata per trasformarlo in un nome
 * inventerebbe un legame che il contratto non dichiara, e legherebbe questa sezione a
 * un'app che oggi puo' essere spenta.
 *
 * E non si ripiega sul `gameMode`: mostrare «1v1» dove l'utente si aspetta «Clash Royale»
 * non e' un fallback, e' un'altra informazione.
 */
export function tournamentGameRef(tournament: unknown): string {
  return asString(asRecord(tournament).gameId);
}

const scheduleOf = (tournament: unknown): LimuloSchedule =>
  asRecord(asRecord(tournament).schedule) as LimuloSchedule;

/**
 * ⚠️ Senza `publishAt` la risposta e' «si mostra». La lista che chiediamo al BFF e' gia'
 * quella pubblica: filtrare qui su un campo assente nasconderebbe righe che il server ha
 * deliberatamente incluso — e un elenco che si accorcia da solo e' indistinguibile da un
 * backend vuoto.
 */
export function isPublished(tournament: unknown, now: number): boolean {
  const publishAt = normalizeInstant(scheduleOf(tournament).publishAt);
  return publishAt === null || publishAt <= now;
}

/**
 * **Una sfida di piattaforma non e' nella lista Tornei della TMA** (I-207, T2-3, L-2362 (a): «la
 * TMA toglie le sfide dalla sua lista Tornei»). La sfida accettata nasce come torneo
 * qTournaments con `origine: 'sfida'` (`sanitizePublicTournament`, contratto PR169 §TORNEI) e vive
 * nel tab «Sfide» (lotto B) — non qui.
 *
 * ⚠️ **SOLO `'sfida'` esclude.** `'community'` e' arrivato nello stesso passthrough e RESTA
 * (dispatch sfide-orcr-3-invio, punto 3: «SOLO quella»); un torneo senza `origine` (bordo di
 * prima di questo campo) o con un valore diverso resta anche lui — l'assenza di un fatto non
 * diventa il fatto «e' una sfida».
 */
export function nonEUnaSfida(tournament: unknown): boolean {
  return (tournament as { origine?: unknown }).origine !== 'sfida';
}

const windowBetween = (openAt: unknown, closeAt: unknown, now: number): LimuloWindow => {
  const open = normalizeInstant(openAt);
  const close = normalizeInstant(closeAt);
  if (open === null && close === null) return 'unknown';
  if (open !== null && now < open) return 'not-yet';
  if (close !== null && now >= close) return 'closed';
  return 'open';
};

export function registrationWindow(tournament: unknown, now: number): LimuloWindow {
  const schedule = scheduleOf(tournament);
  return windowBetween(schedule.registrationOpenAt, schedule.registrationCloseAt, now);
}

/**
 * 🔴 LO STATO D'ISCRIZIONE SECONDO IL SERVER — e **non** secondo il calendario.
 *
 * ⚠️ MISURATO IL 2026-09-06 SU `stg-tma` (or45), ed e' un difetto costato subito: per
 * `ciaocup-ca6975b3` la card mostrava «Iscrizioni: Aperte» — calcolato da
 * `registrationWindow`, cioe' da `registrationOpenAt` (14:56Z, gia' passata) — mentre il
 * server rispondeva `registration: { open: false, status: "scheduled", reason:
 * "window.before-open" }`. **Client e server affermavano stati opposti nello stesso
 * istante**, e lo schermo invitava a un'azione che il backend rifiuta — col 424 muto per
 * giunta.
 *
 * La causa non e' un errore di calcolo: la finestra la decide **anche lo stato del torneo**,
 * non solo le date. `finestraDi` (`qTournaments core/iscrizioni.ts:340-362`) chiude su
 * `classe === 'prima'` PRIMA di guardare `registrationOpenAt` — e su staging lo scheduler non
 * aveva mai fatto avanzare il torneo da `published`. Il calendario, da solo, non poteva
 * saperlo.
 *
 * 🔑 E il contratto lo dice espressamente. Da `registrazioneDa`
 * (`superficie/capability/tornei.ts:174-195`): *«`open` e' ridondante con `status === 'open'`
 * per chi legge, e resta perche' e' **la domanda vera**: un client che deve decidere se
 * mostrare il pulsante non deve dedurla da un'enumerazione.»* Il campo esiste APPOSTA perche'
 * noi non deduciamo.
 *
 * ⇒ **Il calendario e' informazione («apre alle …»), non un verdetto.** Quando il server non
 * ha parlato la risposta e' `unknown`, mai `open`: affermare un'apertura che nessuno ha
 * dichiarato e' la stessa famiglia del vuoto affermato che questa sezione paga da sempre.
 *
 * Il vocabolario di `status` e' quello di `StatoFinestra` (`core/iscrizioni.ts:130-150`):
 * `not_published` · `scheduled` · `open` · `closed` · `full`.
 */
export type LimuloRegistrationState =
  | 'open'
  | 'scheduled'
  | 'closed'
  | 'full'
  | 'not-published'
  /** Il server non ha mandato il blocco, o l'ha mandato senza `open` booleano. */
  | 'unknown';

const STATO_SERVER: Record<string, LimuloRegistrationState> = {
  not_published: 'not-published',
  scheduled: 'scheduled',
  closed: 'closed',
  full: 'full',
};

export function registrationStateOf(tournament: unknown): LimuloRegistrationState {
  const blocco = asRecord(asRecord(tournament).registration);
  const open = blocco.open;
  // ⚠️ Solo un BOOLEANO conta. Una stringa `"false"`, un `null` o l'assenza sono «non l'ha
  // detto» — e trattarli come una risposta significherebbe leggere un'ignoranza come un
  // fatto, che e' esattamente il difetto di cui sopra a parti invertite.
  if (typeof open !== 'boolean') return 'unknown';
  if (open) return 'open';
  // `open: false` E' gia' la risposta; `status` serve solo a dire QUALE chiusura. Uno
  // `status` che non conosciamo non annulla il `false`: resta una chiusura, senza il nome.
  return STATO_SERVER[asString(blocco.status)] ?? 'closed';
}

/**
 * ⚠️ Resta, e resta **informazione**: «le iscrizioni aprono alle …» e' una cosa utile da
 * mostrare. Non e' un verdetto e non decide nessun bottone — v. `registrationStateOf`.
 */
export function checkInWindow(tournament: unknown, now: number): LimuloWindow {
  const schedule = scheduleOf(tournament);
  return windowBetween(schedule.checkInOpenAt, schedule.checkInCloseAt, now);
}

/**
 * Il torneo PRETENDE il check-in? `true`/`false` solo se il server l'ha detto con un booleano,
 * `null` altrimenti.
 *
 * ⚠️ **Oggi e' sempre `null` sul servito**: il motore emette `checkIn: { required }` al primo
 * livello (`tornei.js:468`), ma `sanitizePublicTournament` e' a chiavi fisse e lo scarta
 * [M `qlash-limulo@origin/staging` `a9253fa`, 2026-09-23]. La chiave passa il bordo con
 * `qlash-limulo#108`. ⛔ Non si deduce dall'assenza di `schedule.checkInOpenAt`: un torneo senza
 * date di check-in non ha DETTO di non pretenderlo.
 *
 * ✅ Un `false` dal motore e' una risposta vera, non un'assenza travestita: `checkInRichiesto`
 * e' `settings.checkInRequired === true` ⇒ sempre booleano [M qtournaments-orc 16:20Z, sul
 * sorgente 1.67.0]. Ci stanno anche i tornei nati prima della 1.57.0 senza la chiave: per
 * quelli, dalla 1.67.0, l'iscrizione VALE come check-in (ADR-0153 §7).
 */
export function checkInRequiredOf(tournament: unknown): boolean | null {
  const required = asRecord(asRecord(tournament).checkIn).required;
  return typeof required === 'boolean' ? required : null;
}

/** La finestra del check-in al torneo: i due capi, in millisecondi, o `null` dove il server tace. */
export interface LimuloFinestraCheckIn {
  opensAt: number | null;
  closesAt: number | null;
}

/**
 * La chiusura del check-in si puo' DIRE? Solo se viene dopo l'apertura (o l'apertura non c'e'). Una regola
 * sola per la barra e per la linea del tempo (revisione 2 di #485, difetto nuovo 1): il calendario del motore
 * ammette apertura == chiusura (o == avvio senza chiusura), e una finestra cosi' il motore non la rispetta —
 * vale il default di 15' dall'apertura vera. Scrivere quella chiusura sarebbe falso.
 */
export function chiusuraCheckInDicibile(opensAt: number | null, closesAt: number | null): boolean {
  return closesAt !== null && (opensAt === null || closesAt > opensAt);
}

/**
 * **QUANDO** si fa il check-in al torneo (kb#303 §2): `checkIn.opensAt`/`closesAt` della proiezione
 * pubblica del torneo, cioe' la finestra come la vedra' lo scheduler. Il motore li emette dalla
 * qTournaments 1.83.1 (`superficie/capability/tornei.js:116-117`); il BFF li passa da
 * `qlash-limulo#169` (`sanitizePublicTournament`, contratto in `docs/CAPABILITIES.md`: «never
 * calculates them from `schedule`»).
 *
 * ⛔ **E' solo INFORMAZIONE.** Il bottone lo decide `checkinPlan` sul verdetto `registration.checkIn.open`,
 * e nessun ramo di questa funzione ci arriva: una finestra letta sul telefono che accendesse o spegnesse
 * il check-in sarebbe una decisione presa al posto del server, con lo scarto d'orologio dentro.
 * ⛔ **Non si ricava da `schedule`**: il contratto lo vieta al BFF, e vale a maggior ragione qui (senza
 * date dichiarate lo scheduler apre 15' prima della chiusura: e' una regola del motore, non nostra).
 * Assente — il BFF servito prima di #169 — o illeggibile ⇒ `null`, e la barra non scrive niente: inerte.
 */
export function checkInWindowOf(tournament: unknown): LimuloFinestraCheckIn {
  const c = asRecord(asRecord(tournament).checkIn);
  return { opensAt: normalizeInstant(c.opensAt), closesAt: normalizeInstant(c.closesAt) };
}

/**
 * «12/64», «12», oppure niente. Mai «12/null» e mai «12/0»: un massimo a zero non e' un
 * massimo, e' un campo che il provider non ha compilato.
 *
 * Lo zero nel conteggio invece si mostra: zero iscritti e' un fatto, non un dato mancante.
 */
export function participantsLabel(tournament: unknown): string {
  const { iscritti, capienza } = participantsCounts(tournament);
  if (iscritti === null) return '';
  return capienza !== null && capienza > 0 ? `${iscritti}/${capienza}` : `${iscritti}`;
}

/**
 * Gli stessi due numeri di `participantsLabel`, **prima** che diventino una stringa.
 *
 * Esiste perche' il verdetto d'iscrizione (`eligibility.ts`) deve confrontarli, e
 * ricavarli da un'etichetta significherebbe fare il parsing di qualcosa che abbiamo
 * appena formattato noi — una misura che ha come prova il proprio soggetto.
 * ⚠️ `null` e' «non c'e' il dato», mai `0`: da un iscritto mancante non si deduce un
 * torneo vuoto.
 */
export function participantsCounts(tournament: unknown): {
  iscritti: number | null;
  capienza: number | null;
} {
  const t = asRecord(tournament);
  return {
    iscritti: asFiniteNumber(t.participantsCount),
    capienza: asFiniteNumber(asRecord(t.limits).maxPlayers),
  };
}

/**
 * Ordine della lista: prima cio' su cui si puo' agire adesso (iscrizioni aperte), poi cio'
 * che aprira', poi cio' che e' chiuso, e in fondo cio' di cui non sappiamo abbastanza.
 * Dentro ogni gruppo, per data d'inizio crescente.
 *
 * Non muta la lista in ingresso e non ne perde pezzi: un torneo che non capiamo finisce in
 * fondo, non fuori. Un ordinamento che scarta e' un filtro travestito.
 *
 * 🔴 **LA CHIAVE PRIMARIA E' LO STATO DEL SERVER, non il calendario** — corretto il
 * 2026-09-06, dopo il caso `ciaocup-ca6975b3` (v. `registrationStateOf`).
 *
 * Un ordinamento non e' una frase, ed e' proprio questo che lo rendeva facile da lasciare
 * indietro: nessuno lo legge come un'affermazione. Ma **la prima posizione di una lista e'
 * un consiglio**, e mettere in cima un torneo che il server dichiara `scheduled` dice
 * «comincia da qui» su una cosa su cui non si puo' agire. E' lo stesso difetto della card,
 * in forma attenuata — e attenuata non vuol dire assente.
 *
 * Il calendario resta come chiave **secondaria**, dentro lo stesso stato: fra due tornei che
 * il server dichiara allo stesso modo, e' un criterio ragionevole e non afferma niente che
 * il server abbia contraddetto.
 */
const REG_STATE_RANK: Record<LimuloRegistrationState, number> = {
  open: 0,
  scheduled: 1,
  // `closed` e `full` stanno insieme: sono due modi di non poter entrare, e distinguerli
  // nell'ordine non aiuta chi scorre — la differenza si legge nella riga, non nella posizione.
  closed: 2,
  full: 2,
  // ⚠️ `not-published` sta con `unknown` e non con `closed`: un torneo non pubblicato non e'
  // «chiuso», e' un torneo che ancora non riguarda nessuno. In fondo, come l'ignoranza.
  'not-published': 3,
  unknown: 3,
};

const WINDOW_RANK: Record<LimuloWindow, number> = {
  open: 0,
  'not-yet': 1,
  closed: 2,
  unknown: 3,
};

export function sortTournaments<T>(list: unknown, now: number): T[] {
  if (!Array.isArray(list)) return [];
  return [...(list as T[])]
    .map((tournament, index) => ({ tournament, index }))
    .sort((a, b) => {
      // 1. Cio' che il SERVER dichiara. ⛔ Non riportare qui `registrationWindow`: le date
      //    da sole non conoscono lo stato del torneo (v. il cappello di `REG_STATE_RANK`).
      const statoRank =
        REG_STATE_RANK[registrationStateOf(a.tournament)] -
        REG_STATE_RANK[registrationStateOf(b.tournament)];
      if (statoRank !== 0) return statoRank;
      // 2. Poi il calendario, ma solo DENTRO lo stesso stato: qui non contraddice nessuno.
      const rank =
        WINDOW_RANK[registrationWindow(a.tournament, now)] -
        WINDOW_RANK[registrationWindow(b.tournament, now)];
      if (rank !== 0) return rank;
      const startA = normalizeInstant(scheduleOf(a.tournament).startAt);
      const startB = normalizeInstant(scheduleOf(b.tournament).startAt);
      if (startA !== startB) {
        if (startA === null) return 1;
        if (startB === null) return -1;
        return startA - startB;
      }
      return a.index - b.index;
    })
    .map((entry) => entry.tournament);
}

// ── Standings: l'unica rotta senza facciata ──────────────────────────────────

/**
 * `qlash.tournaments.standings.read` passa da `handleCapabilityJson`
 * (`router.js:1847-1853`) SENZA sanitizer: esce la forma grezza del provider, e non c'e'
 * un contratto da rispecchiare. Quindi si accettano le forme plausibili, si leggono i
 * nomi di campo piu' probabili, e soprattutto **non si scarta niente**: la riga originale
 * resta allegata in `raw`, cosi' una schermata puo' mostrare cio' che non avevamo
 * previsto invece di far finta che non esista.
 *
 * Una riga senza nome resta una riga: farla sparire farebbe sparire un concorrente.
 */
const firstArray = (raw: unknown): unknown[] => {
  if (Array.isArray(raw)) return raw;
  const record = asRecord(raw);
  for (const key of ['items', 'standings', 'rows', 'results']) {
    if (Array.isArray(record[key])) return record[key] as unknown[];
  }
  return [];
};

export function standingsRows(raw: unknown): LimuloStandingsRow[] {
  return firstArray(raw).map((entry) => {
    const row = asRecord(entry);
    return {
      position: asFiniteNumber(row.position) ?? asFiniteNumber(row.rank) ?? asFiniteNumber(row.place),
      name:
        asString(row.displayName) ||
        asString(row.username) ||
        asString(row.name) ||
        asString(row.participantName),
      points: asFiniteNumber(row.points) ?? asFiniteNumber(row.score),
      raw: row,
    };
  });
}

// ── Iscrizione ───────────────────────────────────────────────────────────────

export type { LimuloTournament, LimuloPrizeBand, LimuloRuleFile };


// ── Il bordo largo, il centro stretto ────────────────────────────────────────
// ⚠️ PERCHE' ESISTE QUESTA SEZIONE: `app-tournaments` (il legacy) fornisce 5 delle 7
// capability di questa sezione a priority **205**, contro il **200** del provider
// nativo (`app-tournaments/manifest.js:249-268`). Oggi e' SPENTO, ma accenderlo
// cambierebbe forma e dati senza che nessuno tocchi il client: le date arriverebbero
// come numeri invece che come stringhe ISO, e i limiti porterebbero zeri legittimi.
//
// L'unione vive SOLO qui. Propagare `number|string|null` dentro le schermate
// significherebbe pagare complessita' per sempre contro un provider che potrebbe non
// essere acceso mai — e la complessita' gratuita e' essa stessa un difetto. Da questa
// riga in giu' esiste solo `number | null`.

/**
 * ⚠️ NESSUN CONSUMATORE OGGI, e la distinzione conta: questa e' una **capacita' in attesa
 * di una superficie**, non un residuo. La forma e' quella che il BFF manda davvero (v. il
 * riferimento qui sotto); manca la schermata che la mostri. Chi la trova non la cancelli
 * come codice morto: «non ancora usata» e «non piu' usata» si assomigliano e portano a
 * decisioni opposte. (Controllo del 2026-09-02.)
 *
 * Un limite dal filo → numero, oppure `null`.
 *
 * Lo **zero passa**: dal legacy e' un valore legittimo, non un campo mancante, e
 * trattarlo come assente cancellerebbe un dato vero. Il negativo no: una capienza
 * negativa non e' un fatto, e' spazzatura. Chi renderizza decide poi cosa farne — e
 * `participantsLabel` continua a non scrivere «12/0», che non e' mai giusto.
 */
export function normalizeLimit(value: unknown): number | null {
  if (typeof value !== 'number' || !Number.isFinite(value) || value < 0) return null;
  return value;
}

// ═════════════════════════════════════════════════════════════════════════════
// GLI STATI DEL SERVER IN PAROLE (L-2301, Luca, 2026-09-26T21:44:53Z)
// ═════════════════════════════════════════════════════════════════════════════
// Reference approvata: `qlash-dev-knowledge/services/qlash-telegram-app/ui/
// 2026-09-26-tornei-stati-in-parole.md`. La regola, UNA per ogni stato che viene dal server:
//   1. valore NOTO   ⇒ a schermo la PAROLA (una chiave di copy, in it e in en), mai il codice;
//   2. valore IGNOTO ⇒ a schermo il valore GREZZO, cosi' com'e' arrivato — mai «Sconosciuto»,
//      mai la stringa vuota: un valore nuovo del server non deve sparire.
// Le mappe stanno QUI, accanto alle enumerazioni del modello, e non nei componenti. Cinque
// posti la usano: la modalita' (qui sotto, `gameModeDisplay`), e in fondo al file lo stato del
// torneo (il badge della locandina), lo stato d'iscrizione (la riga «Iscrizioni»), lo stato del
// partecipante (il tab Iscritti) e lo stato della verifica del profilo (l'esito di «Profilo di
// gioco», #464).
// ⚠️ **Il confronto e' ESATTO e passa da `Object.hasOwn`**, come in `openGame.ts`: con l'indice
// nudo un `status: "constructor"` troverebbe una «chiave» nel prototipo di ogni oggetto.

/**
 * Un valore del server pronto per lo schermo: la chiave di copy se il valore e' NOTO, e sempre il
 * valore com'e' arrivato, che e' cio' che si mostra quando non lo e'. E' la forma che
 * `gameModeDisplay` aveva gia': le altre mappe la adottano invece di inventarne una seconda.
 */
export interface LimuloParola {
  /** La chiave di copy (namespace `limulo`), o `null` se il valore non e' fra i noti. */
  i18nKey: string | null;
  /** Il valore GREZZO: il ripiego, e l'unica cosa che si mostra quando `i18nKey` e' `null`. */
  text: string;
}

/** noto ⇒ la sua chiave; ignoto (o vuoto) ⇒ `null`. Il grezzo viaggia sempre accanto. */
function parolaDi(valore: string, copy: Readonly<Record<string, string>>): LimuloParola {
  return { i18nKey: valore !== '' && Object.hasOwn(copy, valore) ? copy[valore] : null, text: valore };
}

/**
 * Il testo da mettere a schermo per una `LimuloParola`: la parola se il valore e' noto, il grezzo
 * se non lo e'. `t` arriva come PARAMETRO: questo livello resta puro, come `prizeHeadlineOf`.
 * ⚠️ `defaultValue` anche sui noti: ~~se una lingua perdesse la chiave, a schermo tornerebbe il
 * valore del server — non il percorso della chiave, che `t()` stampa quando non la trova.~~
 * 🔴 **Vero solo per l'inglese** (revisione di #453, 5a): i18next ripiega PRIMA su `fallbackLng:
 * 'en'` (`lib/i18n.ts`), quindi a una schermata italiana che perdesse la chiave arriverebbe la
 * parola INGLESE; il `defaultValue`, cioe' il grezzo, arriva solo se manca in tutte e due. Cio' che
 * resta vero, ed e' il motivo del `defaultValue`: il percorso della chiave non compare mai.
 */
export function testoDellaParola(
  parola: LimuloParola,
  t: (key: string, options: { defaultValue: string }) => string,
): string {
  return parola.i18nKey ? t(parola.i18nKey, { defaultValue: parola.text }) : parola.text;
}

/**
 * Le modalita' di gioco che la TMA sa dire in parole.
 *
 * 🔴 **FINO AL 2026-09-26 QUI NON C'ERA NESSUN CATALOGO, e la ragione che lo vietava e' SCADUTA.**
 * La nota su `gameModeDisplay` diceva: *«NESSUN CATALOGO DI CHIAVI NOTE, di proposito. Nel mirror
 * non esiste una sola chiave letterale — sono dati, non codice»*. Oggi c'e': il provider di Clash
 * Royale le dichiara come costanti (`CLASH_ROYALE_MATCH_MODES`,
 * `app-game-clash-royale/backend/provider.js:13-16`) con le loro etichette («1 contro 1» e «2 contro
 * 2», `capability-resolvers.js:68-86`) [M `qlash-limulo@origin/staging` = `7e0eae2`, 2026-09-26].
 * ⇒ Ed e' anche il PERCHE' `regular` restava grezzo a schermo (censimento del 26/09, riga 2b): la
 * funzione costruiva sempre `gameMode.<chiave>` come tentativo, e nei file di lingua non c'era
 * nessuna chiave `gameMode` — il tentativo cadeva sempre sul `defaultValue`, cioe' sul grezzo.
 *
 * ~~⚠️ **Le parole sono le etichette del PROVIDER, non nostre.** Il motore le butta al bordo (la
 * facciata manda `label: gameModeKey`, `policy.js:299-309`), quindi la TMA le riscrive qui.~~
 * 🔴 **Superata dalla revisione di #453 (3b)**: la parola segue il copy che la sezione ha GIA'. La
 * Hub di Clash Royale chiama «1v1» la coda uno contro uno (`queues.ui.kind1v1`, `CodeTorneiPanel`),
 * accanto alla lista dei tornei~~: e' lo stesso concetto, quindi `regular` RIUSA quella chiave invece
 * di aprirne una gemella — due frasi uguali sotto due chiavi divergono alla prima revisione~~, e la
 * Hub direbbe «1v1» sopra una riga che dice «1 contro 1».
 * 🔴 **«Lo stesso concetto» e' FALSO** (seconda passata della revisione di #453): e' la stessa
 * PAROLA su due assi diversi. `kind1v1` e' il TIPO di coda (`kind: 'coda'` contro «Sit&go da N»:
 * quanti iscritti si abbinano, `lib/limulo/code.ts:49-50`); la modalita' dice quanti giocatori per
 * PARTE. Sono due campi distinti della stessa coda (`kind` a `code.ts:131`, `gameModeKey` a
 * `:142`), e si combinano: un Sit&go `regular` si legge «Sit&go da 3» nella Hub e «1v1» sulla riga
 * del suo torneo. Con la chiave condivisa, se la coda cambiasse la sua copy la riga la
 * erediterebbe, falsa. ⇒ `regular` ha la
 * SUA chiave (`gameMode.regular`) con la STESSA GRAFIA della coda, «1v1»; `teamVsTeam` idem
 * («2v2»). ~~Il gate tiene allineata la PAROLA, non la chiave.~~ — 🔴 tolto in #464 (#454): il gate
 * fissa da se' la parola della modalita' (`fissa('modalita', …)`, «1v1»/«2v2» in it ed en), e
 * quella della coda non e' piu' legata a questa (oggi nessun cancello la tiene). Il motore butta
 * l'etichetta al bordo
 * (la facciata manda `label: gameModeKey`, `policy.js:299-309`), quindi la parola la scrive
 * comunque la TMA.
 * ⚠️ **RISCHIO DICHIARATO**: `app-qteams/app/capabilities/opzioni.js:22-25` avverte che la chiave e'
 * PER TITOLO — *«la stessa chiave puo esistere su due giochi e significare cose diverse»*. Le due
 * chiavi qui sono quelle di Clash Royale, e la TMA le traduce su ogni gioco: se un gioco usasse
 * `regular` per un'altra forma, la parola sarebbe sbagliata lì. La cura, allora, e' una mappa per
 * `providerAppKey`, non togliere la regola.
 * ~~⛔ **Le chiavi del backgammon (`backgammon-1pt-rapid` & c., `app-qgammon/backend/provider.js:80`)
 * restano GREZZE, di proposito**: la loro etichetta porta la DURATA («rapid (180s +10)»), che la TMA
 * non mostra per decisione di Luca (L-722, v. `lib/qgammon/queueMode.ts`). Dirle in parole e' una
 * scelta da fare a parte, non un effetto collaterale di questa mappa.~~ 🔄 **La scelta e' stata fatta: L-2543
 * (Luca, 30/09 08:28Z), opzione (b), testuale: «Lunghezza e durata: «Match a 3 punti · rapid» (supera L-722 per
 * i tornei)»** ⇒ v. `LIMULO_BACKGAMMON_MODE_COPY`. ⚠️ Supera L-722 SOLO per i tornei: le code ranked restano
 * senza durata (`lib/qgammon/queueMode.ts`, `tools/verify-coda.ts`).
 */
export const LIMULO_GAME_MODE_COPY: Readonly<Record<string, string>> = {
  regular: 'gameMode.regular',
  teamVsTeam: 'gameMode.teamVsTeam',
};

/**
 * La modalita' del backgammon in parole: la LUNGHEZZA e la DURATA (L-2543, opzione b, 30/09). Le 9 chiavi sono
 * `backgammon-{1,3,5}pt-{blitz,rapid,classic}` (`app-qgammon/backend/provider.js:67-81`, qlash-limulo@9f077b8).
 * La durata si dice con la PAROLA del motore (blitz/rapid/classic), senza i secondi: e' il testo dell'opzione
 * scelta («Match a 3 punti · rapid»), non l'etichetta del motore («… · rapid (180s +10)»). Una chiave fuori da
 * queste resta grezza, come ogni valore che la TMA non conosce (L-2301). Mappa a parte da
 * `LIMULO_GAME_MODE_COPY`: quella tiene le due di Clash Royale, e il gate lo fissa.
 */
export const LIMULO_BACKGAMMON_MODE_COPY: Readonly<Record<string, string>> = {
  'backgammon-1pt-blitz': 'gameMode.backgammon1ptBlitz',
  'backgammon-1pt-rapid': 'gameMode.backgammon1ptRapid',
  'backgammon-1pt-classic': 'gameMode.backgammon1ptClassic',
  'backgammon-3pt-blitz': 'gameMode.backgammon3ptBlitz',
  'backgammon-3pt-rapid': 'gameMode.backgammon3ptRapid',
  'backgammon-3pt-classic': 'gameMode.backgammon3ptClassic',
  'backgammon-5pt-blitz': 'gameMode.backgammon5ptBlitz',
  'backgammon-5pt-rapid': 'gameMode.backgammon5ptRapid',
  'backgammon-5pt-classic': 'gameMode.backgammon5ptClassic',
};

/**
 * Cosa mostrare per la modalita' di gioco.
 *
 * `label` PUO' essere la chiave macchina: `policy.js:248-252` la riempie con
 * `gameModeKey` nel ramo di fallback, e un provider puo' mandare `label === key`. Ma
 * puo' anche essere un'etichetta vera. La regola non e' «ignora label», e': **non
 * assumere che sia leggibile, ma non buttarla**.
 *
 * ~~⚠️ NESSUN CATALOGO DI CHIAVI NOTE, di proposito. Nel mirror non esiste una sola
 * chiave letterale — sono dati, non codice — quindi qualunque elenco sarebbe stato
 * inventato qui, cioe' un'osservazione spacciata per contratto. `i18nKey` e' percio' un
 * TENTATIVO di traduzione, sempre costruito dalla chiave, e `text` e' cio' che si
 * mostra se il tentativo non trova niente. Aggiungere una traduzione domani e'
 * additivo e non tocca questo file.~~
 * 🔴 **SCADUTA IL 2026-09-26, mostrata invece di cancellata** (e' la convenzione del repo): le
 * chiavi letterali nel mirror ci sono, e il perche' sta su `LIMULO_GAME_MODE_COPY`. Da oggi
 * `i18nKey` c'e' **solo per una chiave nota**; per le altre e' `null` e `text` e' il grezzo — la
 * stessa regola delle altre mappe (L-2301). Resta vero il resto: il `label`, se c'e', e' il
 * ripiego al posto della chiave.
 */
export function gameModeDisplay(gameMode: unknown): LimuloParola {
  const mode = asRecord(gameMode);
  const key = asString(mode.key);
  const label = asString(mode.label);
  if (!key) return { i18nKey: null, text: label };
  const i18nKey = parolaDi(key, LIMULO_GAME_MODE_COPY).i18nKey ?? parolaDi(key, LIMULO_BACKGAMMON_MODE_COPY).i18nKey;
  return { i18nKey, text: label || key };
}


/**
 * Lo stato d'iscrizione, con la sua inversione dichiarata.
 *
 * ⚠️ `registration` E' PRESENTE ESATTAMENTE QUANDO NON SEI ISCRITTO.
 * `superficie/capability/iscrizioni.js:190-220`:
 *
 *   NON iscritto → status 'not_registered', registered false, registration {finestra}, party null
 *       ISCRITTO → status 'registered',     registered true,  registration NULL,       party {…}
 *
 * Non e' «a volte manca»: c'e' nel caso opposto a quello in cui servirebbe. E poi
 * `sanitizeRegistrationStatus` (`policy.js:380-389`) fa cadere `party`, quindi del caso
 * «iscritto» al client resta solo `registered` + `participant`.
 *
 * Da qui `hasWindow`: un flag esplicito invece di lasciare che una schermata provi a
 * leggere una finestra che, proprio quando serve, non c'e'. La finestra si legge dalla
 * scheda torneo.
 *
 * ⚠️ E vale anche come rete per la SPEC B: da una riga di `/v1/tournaments/mine` non si
 * puo' dedurre nulla sull'iscrizione — `party` cade anche li' (`policy.js:229`) e la card
 * «mio torneo» e' identica a una pubblica. Chi passasse una riga di torneo a questa
 * funzione ottiene «no», che e' la verita', non una bugia comoda.
 */
export function registrationInfo(raw: unknown): {
  registered: boolean;
  status: string;
  hasWindow: boolean;
} {
  const record = asRecord(raw);
  return {
    registered: record.registered === true,
    status: asString(record.status),
    hasWindow: Object.keys(asRecord(record.registration)).length > 0,
  };
}

/**
 * I SETTE motivi che il bordo ammette sul ritiro — in LETTURA (`withdrawal.reason`, `sanitizeWithdrawal`,
 * `policy.js:483`) e, da kb#267, anche nei RIFIUTI del ritiro (`details.withdrawalReason`,
 * `withdrawal-refusal.js:6-12`) [M `qlash-limulo@origin/staging` = `7b85936`, 2026-10-05]. Il quinto,
 * `ritiro.torneo-finito`, e' arrivato con kb#267: prima un torneo finito cadeva su `null` («non sappiamo»).
 * Gli ultimi due, `ritiro.non-capitano` (403) e `ritiro.carica-non-verificabile` (424), sono di
 * `qlash-limulo#205` (qTournaments 1.107.0): una parte di GRUPPO la ritira il capitano, o chi ha
 * `tournament.enroll` sulla squadra in qTeams; se qTeams non risponde il permesso non si presume.
 *
 * ⚠️ E' una **copia** dell'allowlist del server, non la sua sostituzione: il bordo ha gia'
 * scartato tutto il resto. Esiste qui perche' ogni motivo ha una copy sua, e un motivo senza
 * copy si mostrerebbe a schermo come la propria chiave — `t()` su una chiave inesistente non
 * fallisce a build, stampa la chiave.
 */
export const LIMULO_WITHDRAWAL_REASONS = [
  'ritiro.non-iscritto',
  'ritiro.gia-ritirato',
  'ritiro.squalificato',
  'ritiro.finestra-chiusa',
  'ritiro.torneo-finito',
  'ritiro.non-capitano',
  'ritiro.carica-non-verificabile',
] as const;

export type LimuloWithdrawalReason = (typeof LIMULO_WITHDRAWAL_REASONS)[number];

/**
 * 🔴 **IL VERDETTO SUL RITIRO LO DA' IL SERVER — e questa funzione non lo deduce, lo legge.**
 * `sanitizeWithdrawal` (`policy.js:395-402`, Limulo #99 del 2026-09-20).
 *
 * Tre esiti, e il terzo e' il punto:
 *  · `{open: true, reason: null}` — si puo' ritirare;
 *  · `{open: false, reason}` — non si puo', e il motivo e' uno dei quattro noti;
 *  · **`null`** — non lo sappiamo. Il bordo manda `null` sia quando il campo non c'e' sia
 *    quando c'e' ma e' incoerente (`open: true` con un motivo, un motivo fuori allowlist,
 *    `open` non booleano): e' la stessa forma tutto-o-niente del montepremi, e da qui i due
 *    casi **non si distinguono**.
 *
 * ⛔ **IL `null` NON SI RIEMPIE COL CALENDARIO.** Dedurre «sara' aperto perche' le iscrizioni
 * lo sono» rifarebbe esattamente il difetto del 2026-09-06, a parti invertite: client e
 * server che affermano cose opposte nello stesso istante, con l'utente invitato a premere un
 * bottone che il backend rifiuta — col 424 muto, per giunta.
 *
 * ⚠️ E il motivo si richiude sull'allowlist anche QUI, non per sfiducia nel bordo ma perche'
 * un motivo che non conosciamo non ha una frase: senza questo controllo finirebbe a schermo
 * come chiave i18n grezza.
 */
export function withdrawalOf(
  raw: unknown,
): { open: true; reason: null } | { open: false; reason: LimuloWithdrawalReason } | null {
  const w = asRecord(raw).withdrawal;
  if (!w || typeof w !== 'object' || Array.isArray(w)) return null;
  const record = w as Record<string, unknown>;
  if (record.open === true && record.reason === null) return { open: true, reason: null };
  if (record.open === false) {
    const reason = asString(record.reason);
    if ((LIMULO_WITHDRAWAL_REASONS as readonly string[]).includes(reason)) {
      return { open: false, reason: reason as LimuloWithdrawalReason };
    }
  }
  return null;
}

/**
 * I QUATTRO motivi che il bordo ammette sul check-in AL TORNEO quando `open` e' `false`
 * (`CHECKIN_REASONS`, `policy.js:457-459` @ `qlash-limulo#108`, testa `c4ac436`).
 *
 * ⚠️ Copia dell'allowlist del server, come `LIMULO_WITHDRAWAL_REASONS`, e per la stessa ragione:
 * ogni motivo ha una frase, e un motivo senza frase finirebbe a schermo come chiave i18n.
 * ⛔ `checkin.attivo-altrove` **non c'e' e non va aggiunto**: il bordo non lo dichiara (ADR-0158,
 * qTournaments 1.69.0 lo toglie anche dal motore). Sul motore servito (1.68.0) puo' ancora
 * nascere — e il sanitizer lo trasforma in `checkIn: null`, cioe' nel «non lo sappiamo».
 */
export const LIMULO_CHECKIN_REASONS = [
  'checkin.non-iscritto',
  'checkin.parte-non-attiva',
  'checkin.finestra-chiusa',
  'checkin.gia-registrato',
] as const;

export type LimuloCheckInReason = (typeof LIMULO_CHECKIN_REASONS)[number];

/** Il verdetto sul check-in al torneo, come lo legge `checkInOf`: `open: true` ⇔ nessun motivo. */
export type LimuloCheckIn =
  | { required: boolean; open: true; reason: null; checkedIn: boolean; checkedInAt: string | null }
  | {
      required: boolean;
      open: false;
      reason: LimuloCheckInReason;
      checkedIn: boolean;
      checkedInAt: string | null;
    };

/**
 * 🔴 **IL CHECK-IN AL TORNEO LO DICE IL SERVER — questa funzione lo LEGGE, come `withdrawalOf`.**
 * `sanitizeRegistrationCheckIn` (`policy.js:461-472` @ `qlash-limulo#108`, testa `c4ac436`).
 *
 * Esce un oggetto solo se la forma e' quella che il sanitizer produce; altrimenti **`null`**, che
 * vuol dire «non lo sappiamo»:
 *  · `open`, `checkedIn` o `required` non booleani;
 *  · `open: true` CON un motivo;
 *  · `open: false` con un motivo fuori dai quattro noti (anche `checkin.attivo-altrove`).
 * ⚠️ `required` il sanitizer lo scrive sempre booleano: un valore diverso non e' la sua forma, e
 * qui e' incoerente come gli altri. `checkedInAt` invece e' solo da mostrare: una non-stringa
 * diventa `null` (come fa il sanitizer), e la presenza resta vera senza l'ora.
 *
 * ⚠️ **`null` NON distingue «la chiave non c'e'» da «c'e' e dice `null`»**, esattamente come
 * `withdrawalOf`. La differenza conta per la barra (oggi il bordo la chiave non la porta affatto),
 * e la dice `bordoPortaCheckIn`, non questa funzione.
 *
 * ⛔ Il `null` non si riempie col calendario (`schedule.checkInOpenAt`/`checkInCloseAt`): un
 * bottone acceso dedotto dalle date e' un invito a un rifiuto — lo stesso difetto del 2026-09-06.
 */
export function checkInOf(raw: unknown): LimuloCheckIn | null {
  const c = asRecord(raw).checkIn;
  if (!c || typeof c !== 'object' || Array.isArray(c)) return null;
  const record = c as Record<string, unknown>;
  if (
    typeof record.required !== 'boolean' ||
    typeof record.open !== 'boolean' ||
    typeof record.checkedIn !== 'boolean'
  ) {
    return null;
  }
  const base = {
    required: record.required,
    checkedIn: record.checkedIn,
    checkedInAt: typeof record.checkedInAt === 'string' ? record.checkedInAt : null,
  };
  if (record.open === true) {
    return record.reason === null ? { ...base, open: true, reason: null } : null;
  }
  const reason = asString(record.reason);
  return (LIMULO_CHECKIN_REASONS as readonly string[]).includes(reason)
    ? { ...base, open: false, reason: reason as LimuloCheckInReason }
    : null;
}

/**
 * La busta dello stato d'iscrizione PORTA la chiave `checkIn`? — anche col valore `null`.
 *
 * 🔑 **E' il predicato che separa il servito di oggi da quello con `qlash-limulo#108`**, e non
 * e' il merge di #108 (il deploy di staging non si osserva da qui, il campo si'). Oggi
 * `sanitizeRegistrationStatus` e' a chiavi fisse e la chiave non c'e' ⇒ `false` ⇒ la barra fa
 * esattamente quello che faceva. Con #108 la chiave c'e' SEMPRE (`checkIn:
 * sanitizeRegistrationCheckIn(...)`, `policy.js:498`) ⇒ `true`, anche quando il valore e' `null`.
 * ⚠️ Si guarda la CHIAVE, non il valore: qui l'assenza e il `null` si possono distinguere perche'
 * li produce il BORDO in due versioni diverse, non il provider in due umori diversi.
 */
export function bordoPortaCheckIn(raw: unknown): boolean {
  return 'checkIn' in asRecord(raw);
}

/**
 * La classifica, coi due campi che la facciata non ha mai promesso e che il provider
 * manda comunque (`classifica.js:185-201`): `final` e `unavailable`.
 *
 * ⚠️ `items: []` CON `unavailable` NON E' «nessun risultato»: e' una lista vuota che sa
 * spiegarsi. Trattarli allo stesso modo e' il finto vuoto che questa sezione esiste per
 * non produrre — da qui `isEmpty`, che e' vero solo quando il vuoto e' davvero un vuoto.
 *
 * ⚠️ `final` assente diventa `null` e non `false`: «non lo so» e «non e' definitiva» sono
 * cose diverse, e mostrare un parziale con la faccia dell'ufficiale e' il danno peggiore
 * che una classifica possa fare.
 */
export function standingsOf(raw: unknown): {
  rows: LimuloStandingsRow[];
  unavailable: unknown[] | null;
  final: boolean | null;
  total: number | null;
  isEmpty: boolean;
} {
  const record = asRecord(raw);
  const rows = standingsRows(raw);
  const rawUnavailable = record.unavailable;
  const unavailable =
    Array.isArray(rawUnavailable) && rawUnavailable.length > 0 ? (rawUnavailable as unknown[]) : null;
  return {
    rows,
    unavailable,
    final: typeof record.final === 'boolean' ? record.final : null,
    total: asFiniteNumber(record.total),
    isEmpty: rows.length === 0 && unavailable === null,
  };
}

/**
 * I requisiti d'iscrizione — che NON stanno dove il nome dell'endpoint farebbe pensare.
 *
 * `sanitizeRegistrationRequirements` (`policy.js:391-402`) e' un letterale a cinque chiavi
 * SENZA spread: i dettagli veri (partySize, groupEntry, livelli) non passano da
 * `data.registration` ma dal passthrough `tournament.requirements`
 * (`sanitizePublicTournament`, `policy.js:282-285`) — e il provider nativo lo dice
 * esplicitamente nel proprio commento (`superficie/capability/iscrizioni.js:133-145`).
 * Dal legacy quella chiave non esiste: opzionale, mai data per scontata.
 *
 * `profile` e' passthrough grezzo di un provider TERZO (`policy.js:397`): non ha una forma
 * dichiarata da nessuno, quindi non se ne inventa una qui.
 *
 * 🔴 **E NON e' il profilo dell'utente** [M 2026-09-23]: e' l'esito di
 * `game.profile.requirements.read`, cioe' schema, campi e provider del GIOCO, uguale per tutti.
 * Fino al 2026-09-23 da qui usciva `haProfiloDiGioco` («chiave con contenuto ⇒ un profilo
 * esiste»), e l'idoneita' lo usava come «l'utente ha il profilo»: col provider vivo diceva si'
 * a chiunque. Tolto, non spostato: il dato giusto e' la lista dei profili dell'utente, v.
 * `haProfiloPerGioco` in `lib/limulo/registration.ts`.
 */
export function requirementsOf(raw: unknown): {
  requirements: Record<string, unknown> | null;
  requiredCapabilities: string[];
  profile: unknown;
} {
  const record = asRecord(raw);
  const tournament = asRecord(record.tournament);
  const requirements =
    tournament.requirements !== null &&
    typeof tournament.requirements === 'object' &&
    !Array.isArray(tournament.requirements)
      ? (tournament.requirements as Record<string, unknown>)
      : null;
  return {
    requirements,
    requiredCapabilities: asStringArray(record.requiredCapabilities),
    profile: record.profile ?? null,
  };
}

/**
 * ⚠️ NESSUN CONSUMATORE OGGI, e la distinzione conta: questa e' una **capacita' in attesa
 * di una superficie**, non un residuo. La forma e' quella che il BFF manda davvero (v. il
 * riferimento qui sotto); manca la schermata che la mostri. Chi la trova non la cancelli
 * come codice morto: «non ancora usata» e «non piu' usata» si assomigliano e portano a
 * decisioni opposte. (Controllo del 2026-09-02.)
 *
 * Gli iscritti come la facciata li consegna — cioe' **senza avatar**.
 * `sanitizePublicParticipant` (`policy.js:218-227`) non emette `avatarUrl`, che il
 * provider nativo invece produce; nei match gli avatar ci sono, qui no. La risposta
 * giusta e' mostrarli senza: una chiamata al profilo per riga sarebbe un N+1 su una
 * schermata di lista, per un dato che la facciata ha deciso di non dare.
 *
 * ⚠️ `seed` e `rank` si PASSANO. Oggi il provider li lascia a null, ma il sanitizer non
 * li forza (`?? null`): «sempre null» e' un'osservazione, non un contratto, e inciderla
 * qui produrrebbe un rosso nostro e falso il giorno che vengono popolati.
 */
export function participantsOf(raw: unknown): LimuloParticipant[] {
  if (!Array.isArray(raw)) return [];
  return raw.map((entry) => {
    const p = asRecord(entry);
    return {
      id: asString(p.id) || asString(p.participantId),
      participantId: asString(p.participantId) || asString(p.id),
      displayName: asString(p.displayName),
      username: asString(p.username),
      status: asString(p.status),
      seed: asFiniteNumber(p.seed),
      rank: asFiniteNumber(p.rank),
      addedAt: asString(p.addedAt) || null,
    };
  });
}


// ── Fase 3: l'incontro, il referto, i preferiti ──────────────────────────────
// Le derivazioni delle rotte aggiunte al client (`services/limuloApi.ts`). Stessa regola
// del resto del file: cio' che il server non ha detto non diventa un fatto — e la forma
// esce SEMPRE intera, cosi' una schermata non deve difendersi da campi che mancano.

const asBoolean = (value: unknown): boolean => value === true;

/** `undefined` non e' `false`: «non l'ha detto» e «non e' successo» sono due cose. */
const asBooleanOrNull = (value: unknown): boolean | null =>
  typeof value === 'boolean' ? value : null;

const asList = (value: unknown): unknown[] => (Array.isArray(value) ? value : []);

/** Un conteggio: intero ≥ 0, altrimenti `null`. Lo zero e' un fatto e passa; un non-intero non diventa 0. */
const asCountOrNull = (value: unknown): number | null =>
  typeof value === 'number' && Number.isSafeInteger(value) && value >= 0 ? value : null;

/**
 * Il partecipante dentro un incontro: `sanitizeMatchParticipant` (`policy.js:420-427`).
 * `null` quando la parte non c'e' — un oggetto coi campi vuoti sembrerebbe un giocatore
 * senza nome, mentre `null` dice «non c'e'» (bye e forfeit hanno una parte sola).
 */
function matchParticipantOf(raw: unknown): LimuloMatchParticipant | null {
  if (raw === null || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const p = asRecord(raw);
  const id = asString(p.id) || asString(p.participantId);
  return {
    id,
    participantId: asString(p.participantId) || id,
    displayName: asString(p.displayName),
    username: asString(p.username),
    status: asString(p.status),
    seed: asFiniteNumber(p.seed),
    rank: asFiniteNumber(p.rank),
    addedAt: asString(p.addedAt) || null,
    profileId: asString(p.profileId),
    avatarUrl: asString(p.avatarUrl),
    // 🔴 `isMe` SI COPIA SOLO SE IL SERVER L'HA DETTO, e questo ramo esiste per un difetto
    // trovato mentre lo si scriveva: questo parser era un letterale a chiavi fisse, cioe'
    // **lo scartava** — la stessa forma del `sanitizePublicMatch` del BFF che per un giorno
    // ha ingoiato `isMe`/`myCheckin` (kb#141 §4.3). Il campo arrivava davvero
    // [M 2026-09-08 11:09Z su `stg-tma`: `isMe` false/true sui due lati] e la sezione non
    // l'avrebbe visto mai: `proposalRoleOf` avrebbe risposto `unknown` per sempre, cioe'
    // nessun bottone per nessuno, **con il cancello verde**.
    // ⚠️ `asBoolean` NON va bene qui: farebbe `false` da un campo assente, e «non sono io»
    // e «il server non lo dice» sono i due casi che `proposalRoleOf` esiste per distinguere.
    // Quindi la chiave e' presente **solo** quando il valore e' davvero booleano.
    ...(typeof p.isMe === 'boolean' ? { isMe: p.isMe } : {}),
  };
}

/** `sanitizeMediaAsset` (`policy.js:525-542`). */
function mediaAssetOf(raw: unknown): LimuloMediaAsset {
  const a = asRecord(raw);
  return {
    id: asString(a.id),
    assetId: asString(a.assetId) || asString(a.id),
    name: asString(a.name),
    originalName: asString(a.originalName),
    mimeType: asString(a.mimeType),
    mediaType: asString(a.mediaType),
    // ⚠️ I tre `?? 0` sono RIDONDANTI, e restano: il BFF appiattisce gia' a zero
    // (`policy.js:534-536` dentro `sanitizeMediaAsset`, `:525-542` @91743eb —
    // `size: asset.size ?? 0`). Qui e' difesa in profondita', non una scelta di
    // rappresentazione: se un giorno la risposta arrivasse da un percorso che quel
    // sanitizer non attraversa, un `undefined` diventerebbe `NaN` alla prima
    // formattazione. ⚠️ E NON si sostituiscono con `null`: il server manda `0`, e
    // rappresentare come «non lo so» un valore che il server ha dichiarato sarebbe
    // scrivere una cosa diversa da quella ricevuta.
    // (Il difetto vero — `0` indistinguibile da «dimensione non nota» — e' del
    // contratto, non di questa riga: portato a Fabio come voce di kb#114.)
    size: asFiniteNumber(a.size) ?? 0,
    width: asFiniteNumber(a.width) ?? 0,
    height: asFiniteNumber(a.height) ?? 0,
    previewUrl: asString(a.previewUrl),
    downloadUrl: asString(a.downloadUrl),
    url: asString(a.url),
    sharedUrl: asString(a.sharedUrl),
  };
}

/**
 * Il referto: `sanitizeMatchResult` (`policy.js:429-451`). `null` a incontro non concluso
 * — che non e' un dato mancante, e' «non ancora refertato» (`incontri.js:145-150`).
 *
 * ⚠️ Il punteggio sono DUE numeri, non la stringa `"3:1"` del monolite. Il vincitore si
 * LEGGE (`winnerPlayerId`), non si ricalcola dal punteggio: il provider lo deriva da
 * `outcome` (`incontri.js:82-101`) e una seconda regola qui divergerebbe dalla sua.
 */
function matchResultOf(raw: unknown): LimuloMatchResult | null {
  if (raw === null || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const r = asRecord(raw);
  return {
    matchId: asString(r.matchId),
    roundNumber: asFiniteNumber(r.roundNumber),
    playerA: asString(r.playerA),
    playerB: asString(r.playerB),
    scoreA: asFiniteNumber(r.scoreA),
    scoreB: asFiniteNumber(r.scoreB),
    winnerPlayerId: asString(r.winnerPlayerId) || null,
    resultType: asString(r.resultType),
    source: asString(r.source),
    reason: asString(r.reason),
    recordedAt: asString(r.recordedAt) || null,
    expiresAt: asString(r.expiresAt) || null,
    clientMutationId: asString(r.clientMutationId),
    evidenceMediaAssetIds: asStringArray(r.evidenceMediaAssetIds),
    mediaAssets: asList(r.mediaAssets).map(mediaAssetOf),
  };
}

/** `sanitizeMatchCheckin` (`policy.js:453-461`). */
function matchCheckinOf(raw: unknown): LimuloMatchCheckin {
  const c = asRecord(raw);
  const playerId = asString(c.playerId);
  return {
    playerId,
    participantId: asString(c.participantId) || playerId,
    profileId: asString(c.profileId),
    // Il server manda `checkedIn !== false`: una presenza registrata e' presente.
    checkedIn: c.checkedIn !== false,
    checkedInAt: asString(c.checkedInAt) || null,
    source: asString(c.source),
    clientMutationId: asString(c.clientMutationId),
  };
}

/**
 * I CINQUE esiti che il bordo ammette sulla finestra del check-in dell'incontro
 * (`MATCH_CHECKIN_OUTCOMES`, `policy.js` @ `qlash-limulo#134`, testa `a467d11`).
 * ⚠️ Copia dell'allowlist del server, come `LIMULO_CHECKIN_REASONS`: ogni esito ha una frase, e
 * un esito senza frase finirebbe a schermo come chiave i18n grezza.
 */
export const LIMULO_MATCH_CHECKIN_OUTCOMES = [
  'ready',
  'declined',
  'no-show',
  'coin-flip',
  'double-no-show',
] as const satisfies readonly LimuloMatchCheckInOutcome[];

/** `isoOrNull` del sanitizer: una stringa non vuota, altrimenti `null`. */
const isoOrNull = (value: unknown): string | null =>
  typeof value === 'string' && value.length > 0 ? value : null;

/**
 * 🔴 **IL TEMPO DEL CHECK-IN DI UN INCONTRO LO DICE IL SERVER — questa funzione lo LEGGE.**
 * `sanitizeMatchCheckInState` (`policy.js` @ `qlash-limulo#134`, testa `a467d11`), qTournaments
 * 1.69.0 / ADR-0158. Prende la RIGA dell'incontro (come `checkInOf` prende lo stato d'iscrizione)
 * e legge `matchCheckIn`.
 *
 * Esce un oggetto solo se la forma e' coerente; altrimenti **`null`**, che vuol dire «non lo
 * sappiamo» e a schermo e' **la schermata di prima** (il check-in di sempre, senza finestra):
 *  · non un oggetto, oppure `required`/`open` non booleani (le stesse due guardie del sanitizer);
 *  · `outcome` fuori dai cinque noti (il sanitizer lo manda a `null` intero: qui idem);
 *  · ⚠️ `open: true` CON un esito — il contratto dice «`null` (aperto)». Il sanitizer lo lascia
 *    passare; qui no, perche' una finestra aperta con un esito accenderebbe due bottoni su un
 *    incontro che il server dice gia' deciso;
 *  · ⚠️ `required && open` SENZA una scadenza leggibile. Il sanitizer lo lascia passare; qui no:
 *    `deadlineAt` e' il conto alla rovescia, cioe' la sola cosa che dice a chi guarda quanto
 *    tempo ha per un gesto che, mancato, perde l'incontro a tavolino.
 * I tre istanti, come nel sanitizer, sono stringa non vuota oppure `null`.
 *
 * ⛔ **Il `null` NON si riempie da altri campi** (`checkins[]`, `canCheckIn`, l'orologio): un
 * «non sono pronto» acceso per deduzione e' un forfait regalato. E ⚠️ **assente e `null` non si
 * distinguono qui**, e non serve: in tutti e due i casi la schermata e' quella di oggi.
 */
export function matchCheckInOf(raw: unknown): LimuloMatchCheckInState | null {
  const c = asRecord(raw).matchCheckIn;
  if (!c || typeof c !== 'object' || Array.isArray(c)) return null;
  const record = c as Record<string, unknown>;
  if (typeof record.required !== 'boolean' || typeof record.open !== 'boolean') return null;
  let outcome: LimuloMatchCheckInOutcome | null = null;
  if (record.outcome !== null && record.outcome !== undefined) {
    const known = LIMULO_MATCH_CHECKIN_OUTCOMES.find((o) => o === record.outcome);
    if (known === undefined) return null;
    outcome = known;
  }
  if (record.open && outcome !== null) return null;
  const deadlineAt = isoOrNull(record.deadlineAt);
  if (record.required && record.open && normalizeInstant(deadlineAt) === null) return null;
  return {
    required: record.required,
    deadlineAt,
    open: record.open,
    readyAt: isoOrNull(record.readyAt),
    outcome,
    closedAt: isoOrNull(record.closedAt),
  };
}

/**
 * L'incontro: `sanitizePublicMatch` (`policy.js:463-502`).
 *
 * ⚠️ `status` esce GREZZO. Il provider ne emette due soli — `pending`/`completed`
 * (`incontri.js:187`) — contro gli otto di `EMatchStatus` del monolite: tradurlo verso
 * quel vocabolario significherebbe inventare stati che il server non ha mai dichiarato,
 * e ogni `===` copiato da una schermata Laravel qui e' gia' falso.
 *
 * ⚠️ `roundNumber` assente resta `null` e non `1`: il `m.round || 1` del monolite
 * ammucchia tutti gli incontri in un «Round 1» che nessuno ha mai indetto.
 *
 * ⚠️ I tre `can*` mancanti diventano `false`. E' l'unico default sicuro: un pulsante
 * acceso per omissione invita a un'azione che il writer rifiutera'.
 */
export function matchOf(raw: unknown): LimuloMatch {
  const match = asRecord(raw);
  const players = asRecord(match.players);
  const matchId = asString(match.matchId) || asString(match.id);
  return {
    id: asString(match.id) || matchId,
    matchId,
    tournamentId: asString(match.tournamentId),
    roundNumber: asFiniteNumber(match.roundNumber),
    tableNumber: asFiniteNumber(match.tableNumber),
    status: asString(match.status),
    // ⚠️ `asString` e non un default a `'manual'`: il motore ha il suo default e lo applica
    // lui. Metterne uno qui significherebbe DICHIARARE il modo di un incontro su cui il
    // server ha taciuto — e la riga a schermo che ne discende spegne o accende il referto.
    resultMode: asString(match.resultMode),
    isBye: asBoolean(match.isBye),
    playerA: asString(match.playerA),
    playerB: asString(match.playerB),
    players: { a: matchParticipantOf(players.a), b: matchParticipantOf(players.b) },
    result: matchResultOf(match.result),
    externalMatch:
      match.externalMatch !== null &&
      typeof match.externalMatch === 'object' &&
      !Array.isArray(match.externalMatch)
        ? (() => {
            const e = asRecord(match.externalMatch);
            const id = asString(e.id) || asString(e.externalMatchId);
            return {
              id,
              externalMatchId: asString(e.externalMatchId) || id,
              providerKey: asString(e.providerKey),
              status: asString(e.status),
              instructions:
                e.instructions !== null &&
                typeof e.instructions === 'object' &&
                !Array.isArray(e.instructions)
                  ? (e.instructions as Record<string, unknown>)
                  : null,
              resultSyncStrategy: asString(e.resultSyncStrategy),
            };
          })()
        : null,
    // ⚠️ Copiato INTERO e solo se e' un oggetto: e' opaco per contratto, e un letterale a chiavi
    // fisse qui lo perderebbe come il sanitizer prima di #127 (lo stesso difetto di `isMe`).
    externalData:
      match.externalData !== null &&
      typeof match.externalData === 'object' &&
      !Array.isArray(match.externalData)
        ? (match.externalData as Record<string, unknown>)
        : null,
    // 🔴 Il tempo del check-in (qTournaments 1.69.0, `qlash-limulo#134`). Questo parser e' un
    // letterale a chiavi fisse come il sanitizer che lo scartava: senza questa riga la chiave
    // morirebbe QUI il giorno in cui il bordo la porta — con il cancello verde, come `isMe`.
    matchCheckIn: matchCheckInOf(match),
    // 🔴 Il MIO check-in, dichiarato dal server (`policy.js:612` @ staging). Stesso letterale a
    // chiavi fisse: senza questa riga il campo moriva QUI, ed e' esattamente il difetto per cui chi
    // era gia' pronto vedeva ancora «Non sono pronto» (e prendeva un 409 `checkin.already-present`).
    // ⚠️ `asBooleanOrNull` e NON `asBoolean`: un campo assente che diventa `false` direbbe «non hai
    // fatto il check-in» su un server che non ha parlato — il terzo stato resta `null`.
    myCheckin: asBooleanOrNull(match.myCheckin),
    // Le gare della serie vinte a tavolino (kb#298). Stesso letterale a chiavi fisse: senza questa riga
    // il campo morirebbe QUI, col bordo che lo porta. `null` resta «non lo so», mai uno zero inventato.
    walkoverGames: asCountOrNull(match.walkoverGames),
    // 🔴 La scadenza del risultato dal gioco (kb#343, qT 1.111+, `limulo#206`). Stesso letterale a chiavi
    // fisse: senza questa riga il campo morirebbe QUI, col bordo che lo porta e il cancello verde.
    // ⚠️ NON e' fidato: `scadenzaRisultatoOf` lascia passare solo un ISO CON FUSO valido (normalizzato) e
    // fa `null` di tutto il resto — una stringa che `Date.parse` indovina non e' una scadenza. La chiave
    // arriva a `null` su ogni riga [M 04/10 22:02Z]: conta il VALORE, mai la presenza della chiave.
    resultDeadlineAt: scadenzaRisultatoOf(match.resultDeadlineAt),
    winnerPlayerId: asString(match.winnerPlayerId) || null,
    checkins: asList(match.checkins).map(matchCheckinOf),
    canSubmitResult: asBoolean(match.canSubmitResult),
    canCheckIn: asBoolean(match.canCheckIn),
    canForfeit: asBoolean(match.canForfeit),
  };
}

/**
 * «I miei incontri», filtrati QUI e non dal server.
 *
 * ⚠️ IL MOTIVO, che e' una misura: la facciata SPOGLIA `participantId` dalla query
 * (`stripMatchClientFields`, `router.js:307-320`), quindi `?participant=<id>` — che sul
 * monolite funziona (`TournamentDetail.tsx:161-170`) — qui produce la lista INTERA con
 * 200. Un filtro che non filtra e non lo dice e' peggio di un filtro assente: chi guarda
 * conclude che quegli incontri sono i suoi.
 *
 * Senza un `participantId` si restituisce tutto: «non so chi sei» non puo' diventare
 * «non hai incontri», che a schermo e' una bugia con la faccia di un dato.
 */
export function matchesOfParticipant<T>(list: readonly T[], participantId: string | null | undefined): T[] {
  if (!Array.isArray(list)) return [];
  const id = asString(participantId);
  if (id === '') return [...list];
  return list.filter((entry) => {
    const players = asRecord(asRecord(entry).players);
    return (
      asString(asRecord(players.a).participantId) === id ||
      asString(asRecord(players.b).participantId) === id
    );
  });
}

/**
 * La risposta di `GET`/`POST …/result`, `…/checkin` e `…/forfeit`:
 * `sanitizeMatchResultPayload` (`policy.js:506-523`). Quattro rotte, una forma sola.
 *
 * ⚠️ `checkedIn` resta `null` quando il server non lo manda (`policy.js:510`: e'
 * `undefined`, non `false`). Su un check-in la differenza e' tutta: `false` direbbe
 * all'utente che la sua presenza NON e' stata registrata.
 */
export function matchResultPayloadOf(raw: unknown): LimuloMatchResultPayload {
  const p = asRecord(raw);
  const autoCompletion = asRecord(p.autoCompletion);
  return {
    // kb#298: la busta porta anche le gare vinte a tavolino (`sanitizeMatchResultPayload`).
    walkoverGames: asCountOrNull(p.walkoverGames),
    match: p.match === null || p.match === undefined ? null : matchOf(p.match),
    result: matchResultOf(p.result),
    submitted: asBoolean(p.submitted),
    checkedIn: asBooleanOrNull(p.checkedIn),
    // `ready: false` e' la risposta al «non sono pronto» (`qlash-limulo#134`). Stesso letterale a
    // chiavi fisse di sopra: senza, il campo non arriverebbe mai a nessuno.
    ready: asBooleanOrNull(p.ready),
    checkin:
      p.checkin === null || p.checkin === undefined ? null : matchCheckinOf(p.checkin),
    idempotent: asBoolean(p.idempotent),
    autoGeneratedRound: p.autoGeneratedRound ?? null,
    autoGeneration: p.autoGeneration ?? null,
    autoCompletion:
      p.autoCompletion === null || p.autoCompletion === undefined
        ? null
        : {
            completed: asBoolean(autoCompletion.completed),
            results: autoCompletion.results ?? null,
            error: autoCompletion.error ?? null,
          },
  };
}

/**
 * `POST …/result-media`: `sanitizeResultMediaAttachResult` (`policy.js:559-565`) — il
 * referto di cui sopra PIU' gli asset allegati.
 *
 * ⚠️ Questa rotta e' il SECONDO passo di un flusso a due, non una variante del referto:
 * il server registra l'asset (`media.asset.register`) e poi lo allega (`router.js:1940-1975`).
 * Sul monolite l'immagine si postava sullo stesso `/result` del punteggio: chi porta qui
 * quella schermata deve mandare prima il punteggio.
 */
export function resultMediaAttachOf(raw: unknown): LimuloResultMediaAttach {
  const p = asRecord(raw);
  return {
    ...matchResultPayloadOf(raw),
    assets: asList(p.assets).map(mediaAssetOf),
    attached: asBoolean(p.attached),
  };
}

/** Il gioco: `sanitizePublicGame` (`policy.js:152-163`). */
function gameOf(raw: unknown): LimuloGame {
  const g = asRecord(raw);
  const id = asString(g.id);
  return {
    id,
    gameId: asString(g.gameId) || id,
    name: asString(g.name) || asString(g.label),
    label: asString(g.label) || asString(g.name),
    profileType: asString(g.profileType),
    genre: asString(g.genre),
    websiteUrl: asString(g.websiteUrl),
    platformIds: asStringArray(g.platformIds),
    status: asString(g.status),
    tags: asStringArray(g.tags),
  };
}

/** Il preferito: `sanitizeFavoriteGame` (`policy.js:167-173`). */
function favoriteGameOf(raw: unknown): LimuloFavoriteGame {
  const f = asRecord(raw);
  const id = asString(f.id) || asString(f.favoriteId);
  return {
    id,
    favoriteId: asString(f.favoriteId) || id,
    gameId: asString(f.gameId),
    // `active !== false` e' la regola del server: un preferito e' attivo salvo revoca.
    active: f.active !== false,
    game: gameOf(f.game ?? f),
  };
}

/**
 * ⚠️ `GET /v1/me/favorite-games` risponde un OGGETTO — `{active, favorite, items, games}`
 * dentro `data` (`sanitizeFavoriteGameResult`, `policy.js:178-187`; l'handler usa `sendOk`,
 * non `sendListOk`: `router.js:1683-1685`). Srotolarlo con `unwrapItems` darebbe `[]` con
 * 200, cioe' «nessun preferito» dove la verita' e' «non hai guardato nel posto giusto».
 * E' l'unica delle quindici rotte che sembra una lista e non lo e'.
 */
export function favoriteGamesOf(raw: unknown): LimuloFavoriteGames {
  const f = asRecord(raw);
  return {
    active: asBooleanOrNull(f.active),
    favorite: f.favorite === null || f.favorite === undefined ? null : favoriteGameOf(f.favorite),
    items: asList(f.items).map(favoriteGameOf),
    games: asList(f.games).map(gameOf),
  };
}

// ═════════════════════════════════════════════════════════════════════════════
// LA SCHEDA TORNEO — calendario, limiti, montepremi, regolamento
//
// Le quattro derivazioni che la scheda del 2026-09-20 (reference B1-B4 in
// `qlash-dev-knowledge/services/qlash-telegram-app/ui/`) chiede al livello puro. Stanno
// QUI e non nei componenti per la stessa ragione di tutto il resto del file: una
// derivazione dentro un `.tsx` non la puo' esercitare il cancello, e questa sezione ha gia'
// pagato una volta il prezzo di una funzione giusta che nessuno chiamava.
// ═════════════════════════════════════════════════════════════════════════════

/** I SETTE istanti del calendario, nell'ordine in cui la vita di un torneo li attraversa. */
export const LIMULO_TIMELINE_KEYS = [
  'publishAt',
  'registrationOpenAt',
  'registrationCloseAt',
  'checkInOpenAt',
  'checkInCloseAt',
  'startAt',
  'endAt',
] as const;

export type LimuloTimelineKey = (typeof LIMULO_TIMELINE_KEYS)[number];

/**
 * `past` gia' passato · `now` il prossimo che arriva · `future` dopo di quello ·
 * `undeclared` il server non ha mandato la data.
 */
export type LimuloTimelineState = 'past' | 'now' | 'future' | 'undeclared';

export interface LimuloTimelineEntry {
  key: LimuloTimelineKey;
  /** Epoch ms, oppure `null` — «non lo so», che e' una risposta e si scrive. */
  at: number | null;
  state: LimuloTimelineState;
}

/**
 * Il calendario come LINEA DEL TEMPO: sette voci, sempre tutte e sette.
 *
 * 🔴 **IL PUNTO «ADESSO» E' INFORMAZIONE, NON UN PERMESSO — e questa riga e' il motivo per
 * cui la funzione sta qui invece che dentro un componente.** Il 2026-09-06, su
 * `ciaocup-ca6975b3`, il calendario diceva «iscrizioni aperte» mentre il server rispondeva
 * `scheduled`: **stesso istante, due verita'**, e la card invitava a premere un bottone che
 * il backend rifiutava col 424 muto. Chi accende un bottone resta `registrationStateOf`,
 * cioe' il **server**. ⛔ Nessun chiamante deve derivare un'abilitazione da qui.
 *
 * ⚠️ **Una data che non arriva NON si toglie dalla linea**: diventa `undeclared` e si
 * scrive. Togliere l'evento farebbe sembrare il torneo piu' semplice di com'e', e
 * confonderebbe «non ho chiesto» con «ho chiesto e non me l'hanno data». Il caso «quasi
 * tutte le date mancano» e' quello **comune su staging**, non un limite.
 *
 * ⚠️ **`endAt` compare qui per la prima volta**: fino al 2026-09-20 la TMA non lo leggeva
 * affatto, pur essendo emesso dal sanitizer (`policy.js:272`).
 *
 * 🔑 **`now` si sceglie per TEMPO, non per posizione nell'elenco.** Le sette date sono
 * indipendenti e niente garantisce che il provider le scriva in ordine: prendendo «la prima
 * della lista che e' nel futuro», un `endAt` sbagliato piazzato prima di `startAt`
 * sposterebbe il punto acceso su un evento che non e' il prossimo. Si prende il **minimo
 * fra gli istanti dichiarati strettamente maggiori di `now`** — che e' letteralmente la
 * domanda «cosa viene dopo». Se non ce n'e' nessuno, **nessuna voce e' `now`**: un torneo
 * finito non ha un prossimo passo, e accenderne uno per forza sarebbe un'affermazione.
 */
export function tournamentTimeline(tournament: unknown, now: number): LimuloTimelineEntry[] {
  const schedule = scheduleOf(tournament);
  /*
   * 🔴 Le due voci del CHECK-IN, quando lo `schedule` tace, prendono la finestra che il SERVER ha detto
   * (`checkIn.opensAt`/`closesAt`, kb#303 §2; revisione 1 di #485, difetto 3). Senza date dichiarate lo
   * scheduler apre comunque la finestra (15' prima della chiusura o dell'avvio), e la barra della stessa
   * scheda la scrive: la linea del tempo diceva «Data non dichiarata» sulla stessa domanda. Non e' un
   * calcolo del client: sono date del server, e senza di esse la voce resta «non dichiarata».
   */
  const finestra = checkInWindowOf(tournament);
  // L'apertura RISOLTA (dichiarata, o del server): la chiusura del server si scrive solo se viene dopo di
  // lei, con la stessa regola della barra (`chiusuraCheckInDicibile`; revisione 2, difetto nuovo 1).
  const aperturaRisolta = normalizeInstant(schedule.checkInOpenAt) ?? finestra.opensAt;
  const chiusuraDelServer = chiusuraCheckInDicibile(aperturaRisolta, finestra.closesAt) ? finestra.closesAt : null;
  const istanti = LIMULO_TIMELINE_KEYS.map((key) => ({
    key,
    at:
      normalizeInstant(schedule[key]) ??
      (key === 'checkInOpenAt' ? finestra.opensAt : key === 'checkInCloseAt' ? chiusuraDelServer : null),
  }));

  // Il prossimo: il piu' vicino fra quelli dichiarati e ancora da venire.
  let prossimo: number | null = null;
  for (const { at } of istanti) {
    if (at === null || at <= now) continue;
    if (prossimo === null || at < prossimo) prossimo = at;
  }

  // 🔑 UN SOLO «adesso», anche col pareggio (revisione 2, difetto nuovo 4): senza date dichiarate il motore
  // chiude il check-in all'avvio, quindi «Chiusura check-in» e «Inizio» cadono nello stesso istante. Vince la
  // prima nell'ordine del torneo — la chiusura del check-in, che e' il gesto con la scadenza — e l'altra resta
  // «futuro», che e' vero.
  let adessoDato = false;
  return istanti.map(({ key, at }) => {
    let state: LimuloTimelineEntry['state'];
    if (at === null) state = 'undeclared';
    else if (at === prossimo && !adessoDato) {
      state = 'now';
      adessoDato = true;
    } else state = at <= now ? 'past' : 'future';
    return { key, at, state };
  });
}

export interface LimuloTournamentLimits {
  minPlayers: number | null;
  maxPlayers: number | null;
  minLevel: number | null;
  maxLevel: number | null;
  /**
   * ✅ **Adesso arriva** (2026-09-20). Fino a ieri era `null` **per costruzione**: questa
   * funzione lo scriveva come letterale. ⚠️ Il censimento dava la colpa all'interfaccia
   * `LimuloLimits`, che non dichiarava la chiave — ma `tournamentLimits` legge da `asRecord`,
   * non dal tipo, e un mutante l'ha mostrato (tolta la chiave dal tipo, `tsc` resta verde).
   * **La riga colpevole era qui.**
   *
   * ⚠️ **NON passa da `normalizeLimit` come gli altri quattro, e la differenza e' letta alla
   * fonte**: `tornei.js:444-447` (`qlash-limulo@origin/staging` = `ca8719b`) dichiara che a
   * riposo `maxScore: 0` significa **«nessun tetto»** ed emette `numero(...) || null`. Uno
   * zero reso come limite direbbe «tetto zero», cioe' il contrario. ⇒ qui lo zero diventa
   * `null` insieme al negativo, e sugli altri quattro **resta un valore**.
   */
  maxScore: number | null;
}

/**
 * I limiti del torneo, tutti e cinque, come li mostra la Panoramica.
 *
 * ⚠️ **Lo ZERO passa** (`normalizeLimit`): dal legacy e' un valore legittimo e trattarlo
 * come assente cancellerebbe un dato vero. Il negativo no. Chi rende decide poi cosa farne —
 * e `participantsLabel` continua a non scrivere «12/0», che non e' mai giusto.
 */
export function tournamentLimits(tournament: unknown): LimuloTournamentLimits {
  const limits = asRecord(asRecord(tournament).limits);
  return {
    minPlayers: normalizeLimit(limits.minPlayers),
    maxPlayers: normalizeLimit(limits.maxPlayers),
    minLevel: normalizeLimit(limits.minLevel),
    maxLevel: normalizeLimit(limits.maxLevel),
    // ⚠️ `|| null` e non `normalizeLimit`: sul tetto del punteggio lo ZERO e' «nessun tetto»
    // per dichiarazione del produttore, non un valore. V. il campo sull'interfaccia.
    maxScore: normalizeLimit(limits.maxScore) || null,
  };
}

/**
 * Il montepremi, **coi suoi due vuoti tenuti separati**.
 *
 * 🔴 `null` = il contratto non c'e' — o il provider non l'ha fornito, o l'ha fornito scritto
 * male: `sanitizeTournamentPrizes` e' **tutto-o-niente** e una singola fascia invalida fa
 * tornare `null` per l'intero montepremi, quindi **da qui i due non si distinguono**.
 * `[]` = il server ha parlato e dice che premi non ce ne sono.
 * ⛔ Un vuoto solo per entrambi direbbe a un giocatore «questo torneo non ha premi» **quando
 * nessuno l'ha detto**.
 *
 * ⚠️ **Il campo ASSENTE si legge come `null`, e la scelta e' dichiarata.** Un mirror
 * anteriore a `43fe0f0` (2026-09-09) non emette `premi` affatto: `undefined` e `null` sono
 * distinguibili in JSON, ma vogliono dire la stessa cosa a chi guarda — non abbiamo un
 * contratto di montepremi per questo torneo — e una terza copy inventerebbe una differenza
 * che non cambia niente per chi legge.
 *
 * ⚠️ Le fasce arrivano gia' validate dal bordo: qui si controlla solo di aver ricevuto un
 * array, senza rifare il lavoro del sanitizer — rifarlo peggio e' piu' facile che rifarlo
 * uguale.
 */
export function tournamentPrizes(tournament: unknown): LimuloPrizeBand[] | null {
  const premi = asRecord(tournament).premi;
  if (!Array.isArray(premi)) return null;
  return premi as LimuloPrizeBand[];
}

/**
 * Quanti posti copre una fascia. `1º` ne copre uno, `2º–3º` ne copre due.
 *
 * ⚠️ Si mostra **solo quando e' piu' di uno**: scrivere «1 posto» accanto a «1º» ripete
 * l'informazione con parole diverse, che e' rumore.
 */
export function prizeBandSeats(band: LimuloPrizeBand): number {
  return band.max - band.min + 1;
}

/**
 * La fascia **piu' alta**, cioe' quella che arriva piu' vicino al primo posto — il numero
 * che va nel riquadro «Premio» della testata.
 *
 * ⚠️ Non «la piu' ricca»: confrontare due fasce per importo vorrebbe dire sommare valute
 * diverse, e le valute qui sono **chiavi libere del provider** senza nessun tasso
 * dichiarato. La posizione, invece, e' un ordine che il contratto garantisce.
 * ⚠️ E non si assume che l'array sia ordinato: si prende il minimo di `min`.
 */
export function topPrizeBand(bands: readonly LimuloPrizeBand[] | null): LimuloPrizeBand | null {
  if (!bands || bands.length === 0) return null;
  return bands.reduce((migliore, band) => (band.min < migliore.min ? band : migliore));
}

/**
 * Una chiave con la forma di un GUID (a trattini, o 32 esadecimali di fila): e' un `currencyId`.
 * ⚠️ E' la sola cosa che separa un torneo NUOVO (limulo#192, ADR-0187: la chiave di
 * `premi[].ricompense` e' il `currencyId` della moneta) da uno VECCHIO (il `code`, per esempio
 * «pts», senza migrazione). Un code non ha mai questa forma, quindi non serve una lista di codici.
 */
const FORMA_GUID = /^(?:[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}|[0-9a-f]{32})$/i;
export const eUnGuid = (chiave: string): boolean => FORMA_GUID.test(chiave);

/** Una moneta del catalogo della facciata, ridotta a cio' che serve per l'etichetta. */
export interface MonetaDelCatalogo {
  currencyId: string;
  code: string;
}

/**
 * Il torneo ha almeno una chiave di ricompensa con la forma di un GUID? ⇒ SOLO allora serve il
 * catalogo delle monete: sui tornei vecchi nessuna chiamata in piu'.
 */
export function haChiaveGuid(tournament: unknown): boolean {
  return (tournamentPrizes(tournament) ?? []).some((fascia) =>
    Object.keys(fascia.ricompense).some(eUnGuid),
  );
}

/**
 * ⛔ **UN GUID NON ARRIVA MAI A SCHERMO** — la UNICA risoluzione chiave → etichetta, letta dal tab
 * Montepremi e dalla statistica «Premio» (`prizeHeadlineOf`): due copie divergono alla prima
 * revisione. Tre uscite:
 * - chiave NON GUID (torneo vecchio) ⇒ com'e', un code (`codice: true`);
 * - `currencyId` presente nel catalogo ⇒ il `code` della moneta (`codice: true`);
 * - GUID ma catalogo assente (non chiesto, in lettura, 403/404, rete, wallet spento) o che non lo
 *   contiene ⇒ `generica()`, l'etichetta tradotta (`codice: false`).
 * ⚠️ Si cerca per `currencyId` senza badare alle maiuscole: l'identita' e' quella, `code`/`name` sono
 * mutabili. `generica` e' una funzione perche' il testo si chiede solo quando serve, e questo livello
 * resta puro: il catalogo arriva come ARGOMENTO, la lettura la fa chi disegna (`CatalogoMonete`).
 */
export function currencyLabelOf(
  chiave: string,
  catalogo: readonly MonetaDelCatalogo[] | undefined,
  generica: () => string,
): { testo: string; codice: boolean } {
  if (!eUnGuid(chiave)) return { testo: chiave, codice: true };
  const moneta = catalogo?.find((m) => m.currencyId.toLowerCase() === chiave.toLowerCase());
  return moneta ? { testo: moneta.code, codice: true } : { testo: generica(), codice: false };
}

/**
 * Il montepremi in UNA riga — il riquadro «Premio» della testata e la riga condivisa
 * `LimuloTournamentCard` (lista, mie iscrizioni, storico, lobby backgammon).
 *
 * 🔴 **I DUE VUOTI RESTANO DUE**, e stanno qui perche' i due posti che li scrivono non
 * divergano: `null` → «non dichiarato» (il server non l'ha detto), `[]` → «nessuno» (l'ha detto).
 * Collassarli in un «—» direbbe «questo torneo non ha premi» quando nessuno l'ha affermato.
 * ⚠️ La valuta e' il `code` GREZZO (nessun tasso dichiarato; con una chiave `currencyId` la risolve
 * `currencyLabelOf`, che e' anche cio' che legge il tab Montepremi) e il «+N» dice
 * quante altre ce ne sono nella stessa fascia senza sommarle. `t` e `lingua` arrivano come PARAMETRI:
 * questo livello resta puro, come `participantLabelOf`. `lingua` e' quella dell'app, e dice i separatori
 * dell'importo («1.500» in it, «1,500» in en, anche a 4 cifre: `useGrouping: 'always'`, come `formatNumero` in `lib/fmt.ts`, che questo livello puro non importa): senza lingua, `toLocaleString` userebbe quella del browser.
 */
export function prizeHeadlineOf(
  tournament: unknown,
  t: (key: string) => string,
  lingua: string,
  /** Il catalogo delle monete, se c'e': senza, una chiave GUID esce come etichetta generica. */
  catalogo?: readonly MonetaDelCatalogo[],
): { value: string; muted: boolean } {
  const premi = tournamentPrizes(tournament);
  const fasciaAlta = topPrizeBand(premi);
  if (fasciaAlta) {
    const voci = Object.entries(fasciaAlta.ricompense);
    if (voci.length > 0) {
      const [chiave, importo] = voci[0];
      const valuta = currencyLabelOf(chiave, catalogo, () => t('prizes.currencyUnknown')).testo;
      const resto = voci.length > 1 ? ` +${voci.length - 1}` : '';
      return { value: `${importo.toLocaleString(lingua, { useGrouping: 'always' as unknown as boolean })} ${valuta}${resto}`, muted: false };
    }
  }
  return premi === null
    ? { value: t('prizes.stat.undeclared'), muted: true }
    : { value: t('prizes.stat.none'), muted: true };
}

/** Il regolamento come lo mostra il tab, con l'istante gia' normalizzato. */
export interface LimuloRulesView {
  title: string;
  content: string;
  files: LimuloRuleFile[];
  /** `null` = «senza versione», cioe' un regolamento **non tracciato**: si scrive. */
  version: number | null;
  /** Epoch ms gia' passato da `normalizeInstant`, oppure `null`. */
  snapshotAt: number | null;
}

/**
 * Il regolamento del torneo, oppure `null`.
 *
 * 🔴 **`null` COPRE TRE CASI E NON SI POSSONO DISTINGUERE DA QUI**: il torneo non ha un
 * regolamento · ce l'ha ma non e' `format: 'markdown'` · ce l'ha, e' markdown, ma contenuto
 * vuoto **e** nessun allegato. Il bordo (`sanitizeTournamentRules`) manda lo stesso `null`
 * per tutti e tre ⇒ la copy non puo' accusare l'organizzatore di non averlo scritto.
 *
 * ⚠️ `snapshotAt` arriva come **numero** (epoch ms) e passa da `normalizeInstant` come ogni
 * altro istante della sezione: fuori dalla finestra [2000, 2100) diventa `null`, cioe' «non
 * lo so», invece di un gennaio 1970 mostrato come fatto.
 *
 * ⚠️ Gli allegati **non si rifiltrano**: il bordo accetta gia' solo `http`/`https` e scarta
 * gli URL con credenziali. Qui si controlla solo di aver ricevuto una lista.
 */
export function tournamentRules(tournament: unknown): LimuloRulesView | null {
  const rules = asRecord(tournament).rules;
  if (!rules || typeof rules !== 'object' || Array.isArray(rules)) return null;
  const r = rules as Record<string, unknown>;
  return {
    title: asString(r.title),
    content: asString(r.content),
    files: (Array.isArray(r.files) ? r.files : []) as LimuloRuleFile[],
    version: asFiniteNumber(r.version),
    snapshotAt: normalizeInstant(r.snapshotAt),
  };
}

/**
 * Cosa scrivere sulla riga di un allegato: un'etichetta e un tipo.
 *
 * ⚠️ **`fileTitle` puo' essere la stringa vuota** (il bordo ci ripiega quando il campo non
 * e' una stringa): allora si mostra il **nome del file**, mai una riga senza etichetta. Se
 * anche quello manca, resta l'URL — brutto, ma vero.
 *
 * ⚠️ Il tipo si ricava dall'**estensione** e basta: il bordo non manda un `mimeType` per
 * questi file, e dedurlo dal contenuto vorrebbe dire scaricarlo. Senza estensione, `''` —
 * che chi rende mostra come niente, non come «file».
 */
export function ruleFileDisplay(file: LimuloRuleFile): { label: string; ext: string } {
  let name = '';
  try {
    const path = new URL(file.fileUrl).pathname;
    name = decodeURIComponent(path.slice(path.lastIndexOf('/') + 1));
  } catch {
    // Il bordo garantisce un URL assoluto valido; se un giorno non fosse cosi', l'etichetta
    // ripiega sull'URL grezzo invece di far esplodere il tab.
    name = '';
  }
  const punto = name.lastIndexOf('.');
  const ext = punto > 0 && punto < name.length - 1 ? name.slice(punto + 1).toUpperCase() : '';
  return { label: file.fileTitle.trim() || name || file.fileUrl, ext };
}


// ═════════════════════════════════════════════════════════════════════════════
// LA LISTA TORNEI — paginazione e filtri (reference A1, approvata da Luca I-92)
// ═════════════════════════════════════════════════════════════════════════════

/**
 * Quante righe per pagina. 50 e non 100 (il tetto del server): una pagina che arriva e'
 * meglio di una pagina grande, e su una WebView di telefono cinquanta card sono gia' piu'
 * di quante se ne scorrano.
 *
 * ⚠️ Oggi i tornei pubblicati su staging sono **sei**, quindi questa costante non si e' mai
 * vista lavorare. La paginazione esiste perche' il bordo la dichiara, non perche' qualcuno
 * l'abbia esercitata.
 */
export const LIMULO_PAGE_SIZE = 50;

/**
 * C'e' un'altra pagina da chiedere?
 *
 * 🔑 **DUE FONTI, E LA PRIMA VINCE SEMPRE.**
 *
 * 1. **`totalPages` dichiarato** — e' il server che parla, e basta. Nessuna deduzione.
 * 2. **`totalPages` `null`** — `buildPagination` lo lascia `undefined` quando il provider
 *    non lo manda, che su questo bordo e' il caso probabile. Allora si guarda **se l'ultima
 *    pagina era PIENA**: `ricevute === limite` e' un fatto, e da un fatto si puo' *tentare*.
 *    ⚠️ E' un tentativo dichiarato, non una promessa: se la pagina dopo torna vuota, il
 *    bottone sparisce e **non si afferma niente sul perche'**. Una pagina piena e' l'unico
 *    indizio onesto che ne esista un'altra; l'alternativa — non offrire mai il seguito
 *    quando il server tace — nasconderebbe righe che esistono.
 *
 * ⛔ Una pagina **non piena** chiude la domanda in entrambi i rami: un server che ha
 * risposto con meno righe di quante ne poteva dare ha finito.
 */
export function hasNextPage(input: {
  /** La pagina appena ricevuta, 1-based. */
  page: number;
  /** Il `limit` chiesto. */
  limit: number;
  /** Quante righe ha portato l'ULTIMA pagina, non il totale accumulato. */
  received: number;
  /** `totalPages` dichiarato dal server, o `null` se non l'ha detto. */
  totalPages: number | null;
}): boolean {
  if (input.totalPages !== null) return input.page < input.totalPages;
  return input.limit > 0 && input.received >= input.limit;
}

/**
 * I filtri della lista, come chip scorrevoli — e sono **gli stati che ci sono davvero**.
 *
 * ~~⛔ **Non si chiede al server di filtrare.** `?tournamentStatus` viene inoltrato dal bordo e
 * **ignorato** dal provider nativo, che torna la lista intera con 200: *un filtro che non
 * esclude nulla e' indistinguibile da uno che funziona*. Per la stessa ragione la schermata
 * non offre una ricerca.~~ 🔴 **SCADUTA, mostrata invece di cancellata (I-342, 04/10).** La facciata
 * non inoltra piu' `tournamentStatus` (`app-qlash-api/backend/router.js:191-193` @ `qlash-limulo`
 * `5baeedf`) e il motore filtra davvero `gameId`, `status` (UN valore pubblico), `q`/`search`, `tag`,
 * `gameMode`, `platformId` (`tornei.js:793-823`) — sulla lista pubblica E sulle mie (`/mine`, «gli stessi
 * filtri della lista pubblica», `tornei.js:1099-1103`; dal vivo gameId=100: lista 50 → 11, mine 18 → 8).
 * La lista si chiede per GIOCO (`?gameId=`); questi chip restano lato client: lo stato d'iscrizione e' un
 * calcolo nostro sulla finestra (`registrationStateOf`), non un filtro del server.
 *
 * 🔑 **E si offrono solo i chip che possono selezionare qualcosa.** Un chip «Al completo» su
 * un elenco in cui nessun torneo e' al completo porta a un vuoto — e quel vuoto non e'
 * nessuno dei cinque casi dichiarati: e' un sesto, prodotto dalla schermata stessa. Offrire
 * meno chip e' preferibile a inventare un vuoto.
 *
 * ⚠️ L'ordine e' quello di `REG_STATE_RANK`, cioe' lo stesso con cui la lista si ordina: due
 * ordini diversi per la stessa scala fanno sembrare i chip mescolati a caso.
 */
export function registrationFiltersOf(list: readonly unknown[]): LimuloRegistrationState[] {
  const presenti = new Set(list.map((t) => registrationStateOf(t)));
  return (Object.keys(REG_STATE_RANK) as LimuloRegistrationState[])
    .filter((stato) => presenti.has(stato))
    .sort((a, b) => REG_STATE_RANK[a] - REG_STATE_RANK[b]);
}

// ═════════════════════════════════════════════════════════════════════════════
// LO STORICO (reference A3, approvata da Luca il 2026-09-20, I-100)
// ═════════════════════════════════════════════════════════════════════════════

/**
 * I TRE stati storici, tenuti distinti — il motore filtra su questi.
 *
 * 🔴 **⛔ NON SI APPIATTISCONO IN «Concluso».** Un torneo a cui ti eri iscritto e che e' stato
 * **annullato** non e' un torneo che hai giocato: fonderli farebbe sembrare il passato del
 * giocatore **piu' pieno di com'e'**, ed e' un'affermazione su di lui che nessuno ha fatto.
 *
 * ~~⚠️ Lo `status` che arriva e' quello **interno** del provider (i tre si espandono nei
 * rispettivi stati), quindi questo riconoscimento e' **per prefisso di famiglia** e non una
 * mappa chiusa~~ 🔴 **FALSO a `qlash-limulo@origin/staging` = `7e0eae2`, mostrato invece di
 * cancellato** (revisione di #453, 7c): al bordo esce lo stato PUBBLICO (`publicStateOf`;
 * `superficie/capability/tornei.js:5-11`: *«Qui `state` non esce mai: esce `status`, che è il
 * pubblico»*), e i tre esiti sono tre valori pubblici (`complete`, `abandoned`, `archived`). La
 * mappa qui sotto E' chiusa, e le altre grafie (`completed`, `canceled`, `cancelled`) sono i
 * vocabolari che lo storico riconosce in piu'. Resta vero il resto: uno stato che non
 * riconosciamo resta `sconosciuto` — e a schermo, dal 26/09 (L-2301), grezzo solo lui: v.
 * `tournamentStatusDisplay`.
 */
export type LimuloStoricoEsito = 'concluso' | 'annullato' | 'archiviato' | 'sconosciuto';

const STORICO_ESITO: Record<string, LimuloStoricoEsito> = {
  complete: 'concluso',
  completed: 'concluso',
  abandoned: 'annullato',
  canceled: 'annullato',
  cancelled: 'annullato',
  archived: 'archiviato',
};

export function storicoEsitoOf(tournament: unknown): LimuloStoricoEsito {
  return STORICO_ESITO[asString(asRecord(tournament).status)] ?? 'sconosciuto';
}

/**
 * ⚠️ **La classifica si apre SOLO sui tornei conclusi.** Su un torneo **annullato** non c'e'
 * nessuna classifica da aprire, e un link che ci porta e' un vicolo cieco con un 424 in fondo
 * — cioe' la schermata direbbe «questa classifica non e' disponibile» di una classifica che
 * non e' mai esistita.
 */
export function storicoHasStandings(tournament: unknown): boolean {
  return storicoEsitoOf(tournament) === 'concluso';
}

/**
 * L'anno di una riga di storico, per il raggruppamento — oppure `null`.
 *
 * ⚠️ **`endAt`, con ripiego su `startAt`.** `endAt` e' **spesso non dichiarato** (v. la
 * Panoramica: e' uno dei sette istanti che su staging mancano quasi sempre), e con **entrambi**
 * assenti la riga della data **non si mostra**: non si inventa un anno per poter raggruppare.
 * ⇒ chi rende deve reggere un `null`, e le righe senza anno stanno insieme in fondo.
 */
export function storicoAnnoOf(tournament: unknown): number | null {
  const schedule = scheduleOf(tournament);
  const at = normalizeInstant(schedule.endAt) ?? normalizeInstant(schedule.startAt);
  return at === null ? null : new Date(at).getUTCFullYear();
}

/**
 * Le righe raggruppate per anno, dal piu' recente.
 *
 * ⚠️ **Il raggruppamento si mostra SOLO quando lo storico attraversa piu' di un anno**: su un
 * elenco tutto dello stesso anno un'intestazione «2026» sopra ogni riga e' rumore. Questa
 * funzione risponde sempre con i gruppi; **e' chi rende a decidere se stamparne le teste**, e
 * la domanda «ce n'e' piu' di uno?» si legge dalla lunghezza.
 *
 * ⚠️ Le righe **senza anno** (nessuna delle due date dichiarata) finiscono in un gruppo
 * `null` **in fondo**: non si mescolano a un anno che nessuno ha detto.
 */
export function storicoPerAnno<T>(
  list: readonly T[],
): { anno: number | null; righe: T[] }[] {
  const gruppi = new Map<number | null, T[]>();
  for (const riga of list) {
    const anno = storicoAnnoOf(riga);
    const esistente = gruppi.get(anno);
    if (esistente) esistente.push(riga);
    else gruppi.set(anno, [riga]);
  }
  return [...gruppi.entries()]
    .map(([anno, righe]) => ({ anno, righe }))
    .sort((a, b) => {
      // Il gruppo senza anno va SEMPRE in fondo, qualunque siano gli altri.
      if (a.anno === null) return 1;
      if (b.anno === null) return -1;
      return b.anno - a.anno;
    });
}

// ═════════════════════════════════════════════════════════════════════════════
// GLI STATI DEL SERVER IN PAROLE — torneo, iscrizione, partecipante (L-2301)
// ═════════════════════════════════════════════════════════════════════════════
// La regola e la forma (`LimuloParola`, `testoDellaParola`) stanno sopra `gameModeDisplay`.
// Qui le tre mappe degli STATI, ognuna accanto all'enumerazione che traduce. Prima di oggi i tre
// componenti stampavano lo stato grezzo, e ognuno portava lo stesso commento: *«tradurlo
// inventerebbe una corrispondenza che nessuno ha dichiarato»*. ⚠️ Vale ancora per un valore che
// NON conosciamo, ed e' per quello che il ripiego e' il grezzo; per i valori noti la
// corrispondenza e' dichiarata — dall'enumerazione del motore, citata sopra ogni mappa.

/**
 * Lo stato del torneo che il bordo manda — il badge della locandina.
 *
 * 🔑 **SEI stati pubblici, non quattordici**: il motore ne ha 14 interni (`TOURNAMENT_STATES`,
 * `persistenza/tournament.model.js:85-100`) ma al bordo esce solo la proiezione pubblica
 * (`publicStateOf`, stesso file `:174-199`; `superficie/capability/tornei.js:5-11`: *«Qui `state`
 * non esce mai: esce `status`, che è il pubblico»*) [M `qlash-limulo@origin/staging` = `7e0eae2`].
 * I sei: `created` (tutto cio' che viene PRIMA dell'avvio — bozza, pubblicato, iscrizioni e
 * check-in aperti o chiusi, pronto, bloccato), `in_progress`, `paused`, `complete`, `abandoned`
 * (che raccoglie anche `canceled`), `archived`.
 *
 * ⚠️ **I TRE TERMINALI NON SI RIELENCANO QUI**: passano da `storicoEsitoOf`, che e' l'enumerazione
 * unica degli stati finali (v. `isTournamentTerminal` in `polling.ts`: *«una seconda lista qui
 * divergerebbe dalla prima alla prima voce aggiunta»*). Cosi' `completed`, `canceled` e `cancelled`
 * — gli altri vocabolari che lo storico riconosce — hanno la parola gratis, e badge e storico non
 * possono dire due cose diverse dello stesso torneo.
 * ⚠️ `created` e' «Non ancora iniziato», non «In preparazione»: copre anche un torneo con le
 * iscrizioni APERTE, e «in preparazione» si legge «non ancora disponibile». E' la parola del
 * motore stesso per quello stato (`tornei.js:106`: *«presentarlo come "non ancora partito"»*).
 */
const TOURNAMENT_STATUS_COPY: Readonly<Record<string, string>> = {
  created: 'tournamentStatus.created',
  in_progress: 'tournamentStatus.in_progress',
  paused: 'tournamentStatus.paused',
};

const TOURNAMENT_ESITO_COPY: Readonly<Record<string, string>> = {
  concluso: 'tournamentStatus.concluso',
  annullato: 'tournamentStatus.annullato',
  archiviato: 'tournamentStatus.archiviato',
};

export function tournamentStatusDisplay(status: unknown): LimuloParola {
  const valore = asString(status);
  const esito = storicoEsitoOf({ status: valore });
  // ⚠️ Anche `esito` passa da `Object.hasOwn`: `storicoEsitoOf` legge la sua mappa con l'indice
  // nudo, e su un `status` come `constructor` non risponde `sconosciuto`.
  if (Object.hasOwn(TOURNAMENT_ESITO_COPY, esito)) {
    return { i18nKey: TOURNAMENT_ESITO_COPY[esito], text: valore };
  }
  return parolaDi(valore, TOURNAMENT_STATUS_COPY);
}

/**
 * Lo stato d'iscrizione DELL'UTENTE — la riga «Iscrizioni» della scheda, accanto alla parola della
 * finestra.
 *
 * I QUATTRO valori di `status` della busta d'iscrizione (`GET …/registration`): `not_registered` ·
 * `registered` · `withdrawn` · `disqualified` (`bustaDiStato`, `superficie/capability/iscrizioni.js:190`
 * [M `qlash-limulo@origin/staging` = `7e0eae2`]: i primi due erano gia' sul filo, gli altri due sono
 * i letterali di `PartyStatus`). Il legacy (205, spento) ne aggiunge di suoi: sono gli ignoti, e
 * restano GREZZI.
 *
 * 🔴 **La riga dice DUE cose diverse, e fino al 26/09 le diceva in due lingue**: la parola («Aperte»,
 * «Chiuse», «Al completo») e' la FINESTRA del torneo (`registrationStateOf` → `REG_STATE_COPY`);
 * il token accanto (`not_registered`, `registered`) e' lo stato d'iscrizione DELL'UTENTE, un'altra
 * domanda con un'altra fonte. ⇒ `REG_STATE_COPY` non e' la mappa di questo valore, e non si riusa.
 *
 * ~~⚠️ **Per i quattro valori noti il token SPARISCE senza una parola al suo posto**, perche' lo stato
 * e' gia' detto in parole dalla barra d'iscrizione sotto: `registered` ⇒ «Iscritto» e «Ci sei — sei
 * iscritto» (`ETICHETTA_AZIONE`/`ELIGIBILITA_COPY`); `not_registered` ⇒ «Iscriviti»; `withdrawn` e
 * `disqualified` ⇒ il motivo del ritiro bloccato (`ritiro.gia-ritirato`, `ritiro.squalificato`).~~
 * 🔴 **FALSO, e mostrato invece di cancellato** (revisione di #453, punti 2 e 7a). Tre ragioni, tutte
 * misurate montando la scheda:
 *  · sui tornei TERMINALI la barra non si monta, e al suo posto c'e' una frase che dell'utente non
 *    dice niente: «Chiuse `disqualified`» diventava un «Chiuse» nudo, proprio dove si arriva dallo
 *    Storico. Lo stesso col ramo dei requisiti in errore, dove la barra non si monta;
 *  · a un iscritto la barra servita NON dice «Iscritto»/«Ci sei»: con `LIMULO_WITHDRAW_AVAILABLE`
 *    acceso mostra «Ritirati dal torneo», che lo lascia intendere e non lo dice;
 *  · con `withdrawal: null` a chi si e' ritirato la barra dice solo «Iscriviti».
 * ⇒ **La scelta del seggio (26/09): lo stato NOTO diventa SEMPRE la sua parola**, a torneo vivo e
 * terminale, e niente dipende piu' da cosa dice la barra. Stesso posto e stesse classi del token di
 * prima: cambia solo il testo.
 * 🔑 Le parole sono le STESSE del partecipante (`participantStatusDisplay`), e con le stesse chiavi:
 * il motore deriva questo `status` dallo stato della parte (`registered` se e solo se `active`,
 * `iscrizioni.js:200`), quindi e' un concetto solo, e due chiavi gemelle divergerebbero.
 */
export const LIMULO_REGISTRATION_STATUS_COPY: Readonly<Record<string, string>> = {
  not_registered: 'registration.notRegistered',
  registered: 'registration.registered',
  withdrawn: 'registration.withdrawn',
  disqualified: 'registration.disqualified',
};

export function registrationStatusDisplay(status: unknown): LimuloParola {
  return parolaDi(asString(status), LIMULO_REGISTRATION_STATUS_COPY);
}

/**
 * Lo stato di un partecipante — il tab Iscritti.
 *
 * I TRE stati di una parte: `active` · `withdrawn` · `disqualified` (`STATI`,
 * `persistenza/party.model.js:371` [M `qlash-limulo@origin/staging` = `7e0eae2`]; il bordo lo passa
 * com'e', `sanitizePublicParticipant`, `policy.js:274`).
 * ⚠️ `active` e' «Iscritto», non «In gara»: la lista si guarda anche a torneo finito, e li' «in
 * gara» sarebbe falso. «Iscritto» e' anche cio' che il motore fa corrispondere ad `active` sulla
 * busta d'iscrizione (`registered` e' vero solo per `active`, `iscrizioni.js:200`) — ed e' per
 * questo che le tre voci RIUSANO le chiavi dello stato d'iscrizione (`registration.*`): un concetto,
 * un posto solo per le sue parole.
 * ~~⚠️ «Ritirato» SI MOSTRA sempre: una lista che nasconde chi si e' ritirato mente sul numero.~~
 * 🔴 **La schermata non puo' mantenerlo** (revisione di #453, 7b): la lista del motore porta SOLO le
 * parti attive (`find({tournamentId, status: 'active'})`, `superficie/capability/tornei.js:846-847`
 * @ `7e0eae2`), quindi `withdrawn` e `disqualified` dal percorso nativo oggi non arrivano. Le due voci
 * restano perche' sono l'enumerazione del motore: se un giorno la lista li portasse, avrebbero gia'
 * la loro parola invece del grezzo. Cio' che la TMA puo' promettere e' solo questo: non filtra
 * nessuno per stato.
 */
const PARTICIPANT_STATUS_COPY: Readonly<Record<string, string>> = {
  active: 'registration.registered',
  withdrawn: 'registration.withdrawn',
  disqualified: 'registration.disqualified',
};

export function participantStatusDisplay(status: unknown): LimuloParola {
  return parolaDi(asString(status), PARTICIPANT_STATUS_COPY);
}

/**
 * Lo stato della VERIFICA di un profilo di gioco (`profileValidation.status`): l'esito di «Crea il
 * profilo» e di «Ricollega» (`LimuloProfileCreate`). E' il gemello del punto 1 di
 * qlash-telegram-app#454, sotto la stessa regola (L-2301: parole per i noti, grezzo per gli ignoti).
 *
 * 🔴 **UNA SOLA PAROLA, e comincia con «non verificato».** `unverified_allowed` vuol dire che la
 * verifica NON e' stata fatta: la chiave Clash Royale dell'organizzazione manca, e le impostazioni
 * ammettono i profili non verificati (`app-game-clash-royale/backend/provider.js:838-843`
 * [M `qlash-limulo@origin/staging` = `fd57be9`, 2026-09-27]). E' lo stato misurato su staging il
 * 06/09, quindi quello che a schermo si vede davvero. La parola tiene la distinzione che il grezzo
 * teneva (v. la nota 3 in testa a `LimuloProfileCreate`): mai «verificato», mai «ok».
 *
 * ⛔ **`valid` resta GREZZO, di proposito.** Lo stesso valore dice fatti diversi secondo il GIOCO, e
 * il piu' forte e' l'unico che una parola come «verificato» direbbe:
 *  · Clash Royale: la proprieta' e' provata — col token (`provider.js:886-892`) o perche' lo era gia'
 *    (la scorciatoia «gia verificato», `:803-813`);
 *  · eFootball e il profilo generico: solo i campi controllati in locale, «senza verifica esterna»
 *    (`app-games/profile-providers/efootball.js:13`, `:26-37`, sopra `generic.js:152-155`);
 *  · Riot: un id dichiarato, o un account riletto per nome (`profile-providers/riot.js:286-295`,
 *    `:347-352`), senza prova di proprieta'.
 * ~~dopo una `sync` [...] la sync di Clash Royale rende `valid` quando rilegge il giocatore, senza
 * provare niente~~ — 🔴 **falso per Clash Royale** (prima revisione di #464, 1.3): la sync riscrive
 * davvero lo stato (`app-games/runtime-services.js:1541-1543`), ma parte solo dopo una `validate`
 * riuscita sul profilo salvato (`:1502`), e senza token la `validate` di CR passa solo dalla
 * scorciatoia «gia verificato». ⇒ Su CR un `valid` resta legato alla proprieta' provata [D, letto nel
 * sorgente, non osservato]. ⚠️ Chi apre la mappa per gioco parta da qui, non dalla riga barrata.
 * ⇒ Con una mappa GLOBALE, «verificato» sarebbe falso su eFootball e su Riot, nel verso che
 * rassicura. Il predicato della proprieta' e' `ownershipVerified`, non questo stato (v.
 * `limuloProfileOwnershipVerified`). Una parola per `valid` richiede una mappa per gioco: e' la
 * stessa decisione aperta del punto 2 di #454 (la modalita' per gioco).
 * ⚠️ **Perimetro dichiarato** (prima revisione di #464, 1.5): altri valori NOTI possono arrivare qui e
 * restano grezzi per scelta di perimetro, non per sicurezza — `needs_sync` (Riot) da crea/ricollega;
 * dal ramo `already-there`, cioe' da un profilo salvato dopo una sync, `not_configured`,
 * `rate_limited`, `not_found`, `auth_failed`, `provider_error`. (Non `invalid`: la sync parte solo
 * dopo una `validate` riuscita sullo stesso profilo, e crea/ricollega non ne salvano uno —
 * seconda revisione di #464, nota 1.)
 * ⚠️ E `unverified_allowed` dice il TENTATIVO, non il profilo: ricollegando un profilo CR gia'
 * verificato (stesso tag, chiave assente) il motore conserva `ownershipVerified: true` e rende
 * comunque `unverified_allowed` (1.6). La frase resta vera per quel tentativo, e sbaglia nel verso
 * prudente.
 * ⛔ Anche `active` (il profilo backgammon, `app-qgammon/backend/provider.js:376-378`) resta grezzo:
 * e' il valore di un solo provider, e in quello spazio di nomi e' anche lo stato del PROFILO.
 */
export const LIMULO_PROFILE_VALIDATION_STATUS_COPY: Readonly<Record<string, string>> = {
  unverified_allowed: 'profileCreate.validationStatus.unverifiedAllowed',
};

export function profileValidationStatusDisplay(status: unknown): LimuloParola {
  return parolaDi(asString(status), LIMULO_PROFILE_VALIDATION_STATUS_COPY);
}
